/**
 * Move Page — Dashboard Grid Components
 *
 * Design concept: A dashboard-style layout inspired by CRM detail pages.
 * The page is organized as a grid of cards. On mobile, cards stack vertically
 * in priority order based on the lifecycle stage. On larger screens (md+),
 * cards are arranged in a 2-column grid.
 *
 * Key features:
 *   - Full-width "Hero Card" at the top with the most critical info + actions
 *   - Lifecycle stage switcher as a segmented control (for visual context,
 *     doesn't change the data — just highlights which stage the move is in)
 *   - Below the hero: a grid of focused data cards, ordered by relevance
 *     to the current stage
 *   - Each card is self-contained with its own header and data
 *   - Commercial vs residential moves show different cards (e.g., "Property
 *     Details" for residential, "Company" for commercial)
 *
 * This design prioritizes information density: you can see a lot of data
 * at a glance without tabs or accordion clicks.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Box,
  Building2,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  DollarSign,
  FileSignature,
  FileText,
  FlaskConical,
  Home,
  LoaderCircle,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  Navigation,
  Pencil,
  Phone,
  Play,
  Plus,
  Send,
  SlidersHorizontal,
  Sparkles,
  StickyNote,
  Truck,
  Undo2,
  User2,
  Users,
  Voicemail,
  Warehouse as WarehouseIcon,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useQuote } from '../../hooks/useQuote';
import { useJob, Job } from '../../hooks/useJobs';
import { useOrgAdminUsers } from '../../hooks/useOrgAdminUsers';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgWarehouses } from '../../hooks/useOrgWarehouses';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import { DateTimePicker } from '../../components/ui/DateTimePicker';
import { AddressSearch, type SelectedPlace } from '../../components/ui/AddressSearch';
import { api } from '../../lib/api';
import { IssueLogModal, type IssueFormData } from '../../components/jobs/IssueLogModal';
import { SignOffCaptureModal, WaiveSignOffModal, SignOffDetailModal } from '../../components/jobs/SignOffModal';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { usePaymentRequest } from '../../hooks/usePaymentRequest';
import {
  LifecycleStage,
  LIFECYCLE_STAGE_LABEL,
  LeadStatus,
  LEAD_STATUS_LABEL,
  LEAD_STATUS_CLASSES,
  QuoteStatus,
  QUOTE_STATUS_LABEL,
  QUOTE_STATUS_CLASSES,
  JobStatus,
  JOB_STATUS_LABEL,
  JOB_STATUS_CLASSES,
  JobStage,
  JOB_STAGES,
  JOB_STAGE_LABEL,
  JOB_STAGE_ACTION,
  formatDistance,
  formatDuration,
  Quote,
  OrgUser,
  DiscoverySource,
  InboundChannel,
  DISCOVERY_SOURCE_LABEL,
  INBOUND_CHANNEL_LABEL,
  LeadCommunicationType,
  LeadCommunicationEntry,
  LEAD_COMMUNICATION_TYPE_LABEL,
  QuotePatch,
  Organization,
  RentCastPropertyData,
  Truck as TruckData,
  Warehouse,
} from '../../types/admin';

// ── Data Row ───────────────────────────────────────────────────────────────

function DataRow({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  if (!value) return null;
  return (
    <div className={`flex justify-between items-start gap-3 py-1.5 ${className ?? ''}`}>
      <span className="text-[12px] text-slate shrink-0">{label}</span>
      <span className="text-[13px] text-midnight text-right font-medium">{value}</span>
    </div>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────

const ALL_DISCOVERY_SOURCES: DiscoverySource[] = [
  'google', 'yelp', 'homeadvisor_angi', 'thumbtack', 'nextdoor',
  'social_media', 'repeat_customer', 'referral', 'trade_show_event', 'other',
];

const DISCOVERY_SOURCE_OPTIONS = [
  { value: '', label: 'Select a source…' },
  ...ALL_DISCOVERY_SOURCES.map(s => ({ value: s as string, label: DISCOVERY_SOURCE_LABEL[s] })),
];

const ALL_INBOUND_CHANNELS: InboundChannel[] = [
  'manual_entry', 'booking_form', 'website_form', 'phone_call_inbound',
  'walk_in', 'partner_broker',
];

const INBOUND_CHANNEL_OPTIONS = [
  { value: '', label: 'Select a channel…' },
  ...ALL_INBOUND_CHANNELS.map(c => ({ value: c as string, label: INBOUND_CHANNEL_LABEL[c] })),
];

const MOVE_TYPE_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'local', label: 'Local' },
  { value: 'intrastate', label: 'Long Distance (In-State)' },
  { value: 'interstate', label: 'Out of State' },
];

const ALL_COMMUNICATION_TYPES: LeadCommunicationType[] = [
  'phone_call', 'email', 'text_message', 'in_person',
  'voicemail', 'follow_up_scheduled', 'note', 'other',
];

const COMMUNICATION_TYPE_ICON: Record<LeadCommunicationType, typeof Phone> = {
  phone_call: Phone,
  email: Mail,
  text_message: MessageSquare,
  in_person: Users,
  voicemail: Voicemail,
  follow_up_scheduled: CalendarClock,
  note: StickyNote,
  other: MoreHorizontal,
};

function getDefaultFollowUp(): string {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

function getTodayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// (LeadTab type removed — details are now the main page content, activity is a sub-page)

// ── Editable Field Components ─────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-bold uppercase tracking-widest text-slate">{label}</label>
      {children}
    </div>
  );
}

function TextFieldEdit({ value, onChange, placeholder, type = 'text', inputMode, disabled }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'numeric' | 'decimal' | 'tel' | 'email';
  disabled?: boolean;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);

  return (
    <input type={type} inputMode={inputMode} value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onChange(local); }}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full h-10 px-3 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate
                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150
                 ${disabled ? 'opacity-50 cursor-not-allowed bg-mist' : ''}`} />
  );
}

function ToggleField({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
        ${value ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0
        ${value ? 'border-plum bg-plum' : 'border-stone'}`}>
        {value && (
          <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2 text-white">
            <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className={`text-small font-medium ${value ? 'text-plum' : 'text-midnight'}`}>{label}</span>
    </button>
  );
}

function DateInput({ value, onChange, label, min }: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  min?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const formatted = value ? (() => {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  })() : null;

  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-[11px] font-bold uppercase tracking-widest text-slate">{label}</label>}
      <div className="relative h-10 rounded-lg border border-stone bg-white flex items-center px-3 gap-2
                      focus-within:border-plum focus-within:ring-2 focus-within:ring-plum/20
                      transition-colors duration-150">
        <input ref={inputRef} type="date" value={value}
          min={min}
          onChange={e => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
        <span className={`flex-1 text-small ${formatted ? 'text-midnight' : 'text-slate italic'}`}>
          {formatted ?? 'Set date'}
        </span>
        <Calendar size={16} className="text-slate shrink-0" />
      </div>
    </div>
  );
}

function NotesField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <textarea value={local} onChange={e => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onChange(local); }}
      placeholder="Add notes about this lead…" rows={4}
      className="w-full rounded-xl border border-stone bg-white px-4 py-3 text-small text-midnight
                 placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                 focus:border-plum resize-none" />
  );
}

// ── Bottom Sheet ──────────────────────────────────────────────────────────

export function BottomSheet({ open, onClose, title, children }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone">
          <h3 className="text-[14px] font-bold text-midnight">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-mist transition-colors">
            <X size={18} className="text-slate" />
          </button>
        </div>
        <div className="px-4 py-3">{children}</div>
      </div>
    </div>
  );
}

// ── Status Sheet ──────────────────────────────────────────────────────────

export function StatusSheet({ stage, currentStatus, onSelect, onClose }: {
  stage: LifecycleStage;
  currentStatus: string;
  onSelect: (status: string) => void;
  onClose: () => void;
}) {
  let options: Array<{ value: string; label: string; classes: string }> = [];
  if (stage === 'lead') {
    options = (Object.keys(LEAD_STATUS_LABEL) as LeadStatus[]).map(s => ({
      value: s, label: LEAD_STATUS_LABEL[s], classes: LEAD_STATUS_CLASSES[s],
    }));
  } else if (stage === 'quote') {
    options = (Object.keys(QUOTE_STATUS_LABEL) as QuoteStatus[]).map(s => ({
      value: s, label: QUOTE_STATUS_LABEL[s], classes: QUOTE_STATUS_CLASSES[s] ?? 'bg-stone text-slate',
    }));
  } else {
    options = (Object.keys(JOB_STATUS_LABEL) as JobStatus[]).map(s => ({
      value: s, label: JOB_STATUS_LABEL[s], classes: JOB_STATUS_CLASSES[s],
    }));
  }

  return (
    <div className="flex flex-col gap-1">
      {options.map(opt => (
        <button key={opt.value} type="button"
          onClick={() => { onSelect(opt.value); onClose(); }}
          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-colors
            ${opt.value === currentStatus ? 'bg-plum/10 ring-1 ring-plum/30' : 'hover:bg-mist'}`}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${opt.classes}`}>
            {opt.label}
          </span>
          {opt.value === currentStatus && <span className="text-[11px] text-plum ml-auto">Current</span>}
        </button>
      ))}
    </div>
  );
}

// ── User Picker Sheet ─────────────────────────────────────────────────────

export function UserPickerSheet({ users, currentUid, onSelect, onClose }: {
  users: OrgUser[];
  currentUid?: string;
  onSelect: (user: OrgUser) => void;
  onClose: () => void;
}) {
  const activeUsers = users.filter(u => u.status === 'active');
  return (
    <div className="flex flex-col gap-1">
      {activeUsers.length === 0 && <p className="text-[13px] text-slate italic py-2">No users available</p>}
      {activeUsers.map(user => (
        <button key={user.uid} type="button"
          onClick={() => { onSelect(user); onClose(); }}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors
            ${user.uid === currentUid ? 'bg-plum/10 ring-1 ring-plum/30' : 'hover:bg-mist'}`}>
          <div className="w-8 h-8 rounded-full bg-plum/10 flex items-center justify-center text-[11px] font-bold text-plum uppercase shrink-0">
            {(user.displayName || user.email)[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-midnight truncate">{user.displayName || user.email}</p>
            {user.displayName && <p className="text-[11px] text-slate truncate">{user.email}</p>}
          </div>
          {user.uid === currentUid && <CheckCircle2 size={16} className="text-plum shrink-0" />}
        </button>
      ))}
    </div>
  );
}

// ── Inline DateTime Picker ────────────────────────────────────────────────

// ── Hero Card ──────────────────────────────────────────────────────────────

export function HeroCard({ quote, stage, job, versionsCount, onStatusClick, onLeadClick, onScheduleEstimate, onLogActivity, onViewComms, onLogIssue, onStartJob, startingJob, onViewSignOff, onAdvance, onRevert, onRequestSignOff, onRequestPayment, advancing, reverting, updateQuote, navigate, trucks, warehouses, onCheckOutTruck, onCheckInTruck, onAssignTruck }: {
  quote: Quote;
  stage: LifecycleStage;
  job: Job | null;
  versionsCount: number;
  onStatusClick: () => void;
  onLeadClick: () => void;
  onScheduleEstimate: () => void;
  onLogActivity: () => void;
  onViewComms: () => void;
  onLogIssue: () => void;
  onStartJob?: () => void;
  startingJob?: boolean;
  onViewSignOff?: () => void;
  onAdvance?: () => void;
  onRevert?: () => void;
  onRequestSignOff?: () => void;
  onRequestPayment?: () => void;
  advancing?: boolean;
  reverting?: boolean;
  updateQuote: (patch: Record<string, unknown>) => Promise<unknown>;
  navigate: ReturnType<typeof useNavigate>;
  trucks?: TruckData[];
  warehouses?: Warehouse[];
  onCheckOutTruck?: (truckId: string) => Promise<void>;
  onCheckInTruck?: (truckId: string, warehouseId: string) => Promise<{ stageAdvanced: boolean }>;
  onAssignTruck?: () => void;
}) {
  const isCommercial = quote.type === 'commercial';
  const displayName = isCommercial
    ? (quote.customerCompanyName || quote.pointOfContact?.name || 'Untitled')
    : (quote.pointOfContact?.name || quote.customerCompanyName || 'Untitled');
  const isInProgress = stage === 'job' && job?.jobStatus === 'in_progress' && job?.jobStage !== 'complete';
  const contact = job?.pointOfContact ?? quote.pointOfContact;

  // Status
  let statusLabel = '';
  let statusClasses = '';
  if (stage === 'lead') {
    const ls = quote.leadStatus ?? 'new';
    statusLabel = LEAD_STATUS_LABEL[ls];
    statusClasses = LEAD_STATUS_CLASSES[ls];
  } else if (stage === 'quote') {
    const qs = quote.quoteStatus ?? 'draft';
    statusLabel = QUOTE_STATUS_LABEL[qs];
    statusClasses = QUOTE_STATUS_CLASSES[qs] ?? 'bg-stone text-slate';
  } else {
    const js = quote.jobStatus ?? 'scheduled';
    statusLabel = JOB_STATUS_LABEL[js];
    statusClasses = JOB_STATUS_CLASSES[js];
  }

  // Cost display
  const estimatedValue = quote.estimatedValue;

  return (
    <div className="bg-gradient-to-r from-plum to-rose p-[3px] rounded-xl shadow-brand-md">
      <div className="bg-white rounded-[10px] p-4">
        {/* Top row: name + status + cost */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <button type="button" onClick={onStatusClick}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider hover:opacity-80 transition-opacity ${statusClasses}`}>
                {statusLabel}
                <ChevronDown size={10} />
              </button>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate">
                {LIFECYCLE_STAGE_LABEL[stage]}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-[22px] leading-tight truncate text-midnight">{displayName}</h1>
              {isInProgress && contact?.phone && (
                <a href={`tel:${contact.phone}`}
                  className="w-7 h-7 shrink-0 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
                  aria-label="Call contact">
                  <Phone size={13} />
                </a>
              )}
              {isInProgress && contact?.email && (
                <a href={`mailto:${contact.email}`}
                  className="w-7 h-7 shrink-0 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
                  aria-label="Email contact">
                  <Mail size={13} />
                </a>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {quote.quoteNumber && (
                <span className="text-[12px] text-slate font-mono">{quote.quoteNumber}</span>
              )}
              <span className="text-[11px] text-slate/70 flex items-center gap-1">
                {isCommercial ? <><Building2 size={11} /> Commercial</> : <><Home size={11} /> Residential</>}
              </span>
            </div>
          </div>
          {/* Cost — lead shows estimated value, quote/job shows pre-tax subtotal */}
          {stage === 'lead' && estimatedValue != null && estimatedValue > 0 && (
            <div className="text-right shrink-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate">Est.</p>
              <p className="text-[22px] font-display leading-tight text-midnight">
                {estimatedValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
              </p>
            </div>
          )}
          {stage === 'job' && (() => {
            const outstandingCents = job?.paymentSummary?.outstandingAmountCents;
            if (outstandingCents != null && outstandingCents > 0) {
              return (
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate">Balance</p>
                  <p className="text-[22px] font-display leading-tight text-midnight">
                    {(outstandingCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* ── Lead-specific content ────────────────────────────────────── */}
        {stage === 'lead' && (
          <>
            {/* Move Date + Follow-Up — side-by-side callout boxes */}
            <div className="flex gap-2 mt-3">
              {/* Move Date callout */}
              <div className="flex-1 min-w-0 bg-plum/5 border border-plum/20 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                <Calendar size={16} className="text-plum shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-plum">Move Date</p>
                  <p className="text-small font-semibold text-midnight truncate">
                    {quote.movingDate?.preferredDate
                      ? new Date(quote.movingDate.preferredDate.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                      : quote.estimatedMoveDateRange
                        ? quote.estimatedMoveDateRange
                        : 'Not set'}
                  </p>
                </div>
              </div>

              {/* Next Follow-Up callout */}
              {(() => {
                const isOverdue = quote.nextFollowUp ? new Date(quote.nextFollowUp) < new Date() : false;
                return (
                  <div className={`flex-1 min-w-0 rounded-xl px-3 py-2.5 border flex items-center gap-2.5 ${
                    isOverdue
                      ? 'border-rose/30 bg-rose/5'
                      : quote.nextFollowUp
                        ? 'border-plum/20 bg-plum/5'
                        : 'border-stone bg-mist'
                  }`}>
                    <CalendarClock size={16} className={`shrink-0 ${isOverdue ? 'text-rose' : 'text-plum'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-bold uppercase tracking-wider ${isOverdue ? 'text-rose' : 'text-plum'}`}>
                        {isOverdue ? 'Overdue' : 'Follow-Up'}
                      </p>
                      <p className={`text-small font-semibold truncate ${
                        isOverdue ? 'text-rose' : quote.nextFollowUp ? 'text-midnight' : 'text-slate italic'
                      }`}>
                        {quote.nextFollowUp
                          ? new Date(quote.nextFollowUp + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                          : 'Not set'}
                      </p>
                    </div>
                    <div className="relative shrink-0">
                      <input type="date" value={quote.nextFollowUp ?? ''}
                        onChange={e => updateQuote({ nextFollowUp: e.target.value || undefined })}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      <button type="button"
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                          isOverdue ? 'text-rose hover:bg-rose/10' : 'text-plum hover:bg-plum/10'
                        }`} aria-label="Edit follow-up date">
                        <Pencil size={12} />
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* CTAs: Log + Comms + Schedule Estimate */}
            <div className="flex gap-2 mt-3 pt-3 border-t border-stone">
              <button type="button" onClick={onLogActivity}
                className="flex-1 h-9 rounded-lg bg-mist flex items-center justify-center gap-1.5 text-[12px] font-medium text-midnight hover:bg-plum/10 transition-colors">
                <Phone size={13} className="text-plum" /> Log
              </button>
              <button type="button" onClick={onViewComms}
                className="flex-1 h-9 rounded-lg bg-mist flex items-center justify-center gap-1.5 text-[12px] font-medium text-midnight hover:bg-plum/10 transition-colors">
                <MessageSquare size={13} className="text-plum" /> Comms
              </button>
              <button type="button" onClick={onScheduleEstimate}
                className="flex-1 h-9 rounded-lg bg-plum/10 flex items-center justify-center gap-1.5 text-[12px] font-medium text-plum hover:bg-plum/20 transition-colors">
                <CalendarClock size={13} /> Schedule Estimate
              </button>
            </div>
          </>
        )}

        {/* ── Quote-specific content ───────────────────────────────────── */}
        {stage === 'quote' && (
          <>
            {/* Estimate Visit + Lead — stacked */}
            <div className="mt-3 flex flex-col gap-1.5 text-[12px]">
              <div className="flex items-center gap-1.5">
                <Calendar size={12} className="text-slate shrink-0" />
                <span className="text-slate shrink-0">Estimate:</span>
                <DateTimePicker
                  value={quote.estimateVisitDate ?? ''}
                  onChange={(val) => updateQuote({ estimateVisitDate: val })}
                  placeholder="Schedule estimate"
                  variant="inline"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Users size={12} className="text-slate shrink-0" />
                <span className="text-slate shrink-0">Lead:</span>
                <button type="button" onClick={onLeadClick}
                  className="text-plum font-medium hover:underline flex items-center gap-1 text-[12px]">
                  {quote.quoteLeadDisplayName || 'Assign'}
                  <Pencil size={10} />
                </button>
              </div>
            </div>

            {/* Subtotal — prominent display with a clear Adjust Estimate button */}
            {quote.formula?.finalCostEstimate != null && (
              <div className="mt-3">
                <div className="w-full py-3 rounded-lg bg-plum/5 border border-plum/20
                                flex flex-col items-center gap-0.5">
                  <p className="text-[28px] font-display leading-tight text-plum">
                    {quote.formula.finalCostEstimate.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </p>
                  <span className="text-[11px] text-slate uppercase tracking-wider">Estimated Cost</span>
                </div>
                <button type="button"
                  onClick={() => navigate(`/moves/${quote.id}/breakdown`)}
                  className="w-full mt-2 h-10 rounded-lg border border-plum text-plum text-[13px] font-semibold
                             flex items-center justify-center gap-2
                             hover:bg-plum/5 active:bg-plum/10 transition-colors">
                  <SlidersHorizontal size={14} />
                  Adjust Estimate
                </button>
              </div>
            )}

            {/* Crew / Trucks / Hours counters */}
            {quote.formula && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[
                  { label: 'Crew', value: quote.formula.finalCrewSize, icon: <Users size={13} /> },
                  { label: 'Trucks', value: quote.formula.truckQuantity, icon: <Truck size={13} /> },
                  { label: 'Hours', value: quote.formula.totalHours, icon: <Clock size={13} /> },
                ].map(item => (
                  <div key={item.label} className="bg-mist rounded-lg px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1 text-plum mb-0.5">{item.icon}</div>
                    <p className="text-[16px] font-bold text-midnight">{item.value ?? '—'}</p>
                    <p className="text-[10px] text-slate uppercase tracking-wider">{item.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Move date — clickable inline picker */}
            <div className="flex items-center gap-1.5 mt-3 text-[12px]">
              <Calendar size={12} className="text-slate shrink-0" />
              <span className="text-slate shrink-0">Move:</span>
              <DateTimePicker
                value={quote.movingDate?.preferredDate ?? ''}
                onChange={(val) => updateQuote({ movingDate: { ...quote.movingDate, preferredDate: val || undefined } })}
                placeholder="Set move date"
                variant="inline"
              />
            </div>

            {/* Client Quotes CTA */}
            {(quote.formula?.finalCostEstimate != null || versionsCount > 0) && (
              <button type="button" onClick={() => navigate(`/moves/${quote.id}/client-quotes`)}
                className="w-full mt-3 h-10 rounded-lg bg-gradient-to-r from-plum to-rose text-white text-[13px] font-semibold
                           flex items-center justify-center gap-2 hover:opacity-90 active:opacity-80 transition-opacity">
                <FileText size={14} />
                {versionsCount > 0 ? 'Client Quotes' : 'Generate Client Quote'}
                <ChevronRight size={14} />
              </button>
            )}

            {/* Outcome summary */}
            {quote.outcome && (quote.quoteStatus === 'booked' || quote.quoteStatus === 'declined') && (
              <div className={`mt-3 px-3 py-2 rounded-lg border text-[12px] ${
                quote.quoteStatus === 'booked'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}>
                <span className="font-bold">{quote.quoteStatus === 'booked' ? 'Won' : 'Lost'}:</span>{' '}
                {quote.outcome.reason}
                {quote.outcome.note && <p className="mt-1 text-[11px] opacity-80">{quote.outcome.note}</p>}
              </div>
            )}
          </>
        )}

        {/* ── Job-specific content ─────────────────────────────────────── */}
        {stage === 'job' && (
          <>
            {/* Non-in-progress: key info strip + move lead */}
            {!isInProgress && (
              <>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[12px] text-midnight">
                  {quote.movingDate?.preferredDate && (
                    <span className="flex items-center gap-1.5">
                      <Calendar size={12} className="text-slate" />
                      {new Date(quote.movingDate.preferredDate.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  {quote.formula?.finalCrewSize && (
                    <span className="flex items-center gap-1.5">
                      <Users size={12} className="text-slate" /> {quote.formula.finalCrewSize} crew
                    </span>
                  )}
                  {quote.formula?.truckQuantity && (
                    <span className="flex items-center gap-1.5">
                      <Truck size={12} className="text-slate" /> {quote.formula.truckQuantity} trucks
                    </span>
                  )}
                  {quote.addresses?.transit && (
                    <span className="flex items-center gap-1.5">
                      <Navigation size={12} className="text-slate" /> {formatDistance(quote.addresses.transit.distanceMeters)}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2 text-[12px]">
                  <span className="text-slate">Move Lead:</span>
                  <button type="button" onClick={onLeadClick}
                    className="text-plum font-medium hover:underline flex items-center gap-1">
                    {quote.moveLeadDisplayName || 'Assign'}
                    <Pencil size={10} />
                  </button>
                </div>
              </>
            )}

            {/* Completion date */}
            {job?.jobStatus === 'completed' && job.moveCompletionDate && (
              <div className="mt-2 flex items-center gap-1.5 text-[12px] text-slate">
                <Calendar size={12} />
                Completed: {new Date(job.moveCompletionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            )}

            {/* ── In-progress: stage tracker + info + actions ── */}
            {isInProgress && job && (() => {
              const jobStage = job.jobStage ?? 'scheduled';
              const stageIdx = JOB_STAGES.indexOf(jobStage);
              const actionLabel = JOB_STAGE_ACTION[jobStage];
              const origin = job.addresses?.origin;
              const dest = job.addresses?.destination;
              const transit = job.addresses?.transit;

              return (
                <div className="mt-3">
                  {/* Stage tracker */}
                  <div className="mb-5">
                    <StageTracker currentStage={jobStage} />
                  </div>

                  {/* Stage-specific condensed info */}
                  <div className="flex flex-col gap-2">
                    {/* ── Scheduled ── */}
                    {jobStage === 'scheduled' && (
                      (job.assignedTrucks ?? []).length === 0 ? (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-[13px] text-amber-800">
                            <Truck size={14} className="text-amber-600 shrink-0" />
                            <span>No trucks assigned to this job</span>
                          </div>
                          {onAssignTruck && (
                            <button type="button" onClick={onAssignTruck}
                              className="h-9 px-3 rounded-lg border border-plum/30 bg-plum/5 text-plum text-[12px] font-semibold
                                         flex items-center gap-1.5 self-start hover:bg-plum/10 transition-colors">
                              <Plus size={13} /> Assign Truck
                            </button>
                          )}
                          <button type="button" onClick={onAdvance} disabled={advancing}
                            className="w-full h-11 rounded-xl bg-plum text-white text-[13px] font-semibold
                                       flex items-center justify-center gap-2
                                       hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors">
                            {advancing ? 'Processing...' : 'En Route to Origin'}
                          </button>
                        </div>
                      ) : (
                        <UncheckedTrucksByWarehouse
                          assignedTrucks={job.assignedTrucks ?? []}
                          allTrucks={trucks}
                          warehouses={warehouses}
                          originWarehouseId={quote.travelToJob?.originWarehouseId}
                          onCheckOut={onCheckOutTruck}
                        />
                      )
                    )}

                    {/* ── En Route to Origin ── */}
                    {jobStage === 'en_route_origin' && (() => {
                      const originWhId = quote.travelToJob?.originWarehouseId;
                      const startWarehouse = originWhId ? warehouses?.find(w => w.id === originWhId) : undefined;
                      const whToOriginLeg = originWhId ? quote.warehouseTransit?.[originWhId]?.toOrigin : undefined;
                      const warehouseTravelSecs = whToOriginLeg?.durationSeconds ?? quote.travelToJob?.originTravelSeconds;

                      return (
                        <>
                          {origin && (
                            <div className="flex items-start gap-1.5">
                              <MapPin size={13} className="text-plum shrink-0 mt-0.5" />
                              <a href={`https://maps.google.com/?q=${encodeURIComponent(origin.address)}`}
                                target="_blank" rel="noopener noreferrer"
                                className="flex-1 min-w-0 text-[13px] text-midnight hover:text-plum transition-colors">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate block">Origin</span>
                                {origin.address}
                              </a>
                              <a href={`https://www.google.com/maps/dir/?api=1${startWarehouse ? `&origin=${encodeURIComponent(startWarehouse.address)}` : ''}&destination=${encodeURIComponent(origin.address)}`}
                                target="_blank" rel="noopener noreferrer"
                                className="w-7 h-7 shrink-0 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
                                aria-label="Get directions to origin">
                                <Navigation size={13} />
                              </a>
                            </div>
                          )}
                          {warehouseTravelSecs != null && warehouseTravelSecs > 0 && (
                            <span className="text-[12px] text-slate">
                              ~{formatTransitDuration(warehouseTravelSecs)} from {startWarehouse?.name ?? 'warehouse'}
                            </span>
                          )}
                          <UncheckedTrucksByWarehouse
                            assignedTrucks={job.assignedTrucks ?? []}
                            allTrucks={trucks}
                            warehouses={warehouses}
                            originWarehouseId={quote.travelToJob?.originWarehouseId}
                            onCheckOut={onCheckOutTruck}
                          />
                        </>
                      );
                    })()}

                    {/* ── Loading ── */}
                    {jobStage === 'loading' && (
                      <>
                        <button type="button"
                          onClick={() => navigate(`/moves/${quote.id}/${quote.type === 'commercial' ? 'casegoods' : 'residential-rooms'}`)}
                          className="text-[12px] font-medium text-plum hover:text-rose transition-colors flex items-center gap-1 self-start">
                          <Box size={12} /> Rooms & Inventory
                        </button>
                        <UncheckedTrucksByWarehouse
                          assignedTrucks={job.assignedTrucks ?? []}
                          allTrucks={trucks}
                          warehouses={warehouses}
                          originWarehouseId={quote.travelToJob?.originWarehouseId}
                          onCheckOut={onCheckOutTruck}
                        />
                      </>
                    )}

                    {/* ── En Route to Destination ── */}
                    {jobStage === 'en_route_destination' && (
                      <>
                        {dest && (
                          <div className="flex items-start gap-1.5">
                            <MapPin size={13} className="text-plum shrink-0 mt-0.5" />
                            <a href={`https://maps.google.com/?q=${encodeURIComponent(dest.address)}`}
                              target="_blank" rel="noopener noreferrer"
                              className="flex-1 min-w-0 text-[13px] text-midnight hover:text-plum transition-colors">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate block">Destination</span>
                              {dest.address}
                            </a>
                            <a href={`https://www.google.com/maps/dir/?api=1${origin ? `&origin=${encodeURIComponent(origin.address)}` : ''}&destination=${encodeURIComponent(dest.address)}`}
                              target="_blank" rel="noopener noreferrer"
                              className="w-7 h-7 shrink-0 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
                              aria-label="Get directions to destination">
                              <Navigation size={13} />
                            </a>
                          </div>
                        )}
                        {transit && (
                          <span className="text-[12px] text-slate">
                            ~{formatTransitDuration(transit.durationSeconds)} drive
                          </span>
                        )}
                      </>
                    )}

                    {/* ── Unloading ── */}
                    {jobStage === 'unloading' && (() => {
                      const hasBalance = (job.paymentSummary?.outstandingAmountCents ?? 0) > 0;
                      return (
                        <>
                          <button type="button"
                            onClick={() => navigate(`/moves/${quote.id}/${quote.type === 'commercial' ? 'casegoods' : 'residential-rooms'}`)}
                            className="text-[12px] font-medium text-plum hover:text-rose transition-colors flex items-center gap-1 self-start">
                            <Box size={12} /> Rooms & Inventory
                          </button>
                          {hasBalance && (
                            <button type="button" onClick={onRequestPayment}
                              className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold
                                         flex items-center gap-1.5 self-start
                                         hover:bg-emerald-700 active:opacity-80 transition-colors">
                              <DollarSign size={12} /> Request Payment
                            </button>
                          )}
                        </>
                      );
                    })()}

                    {/* ── Customer Sign-Off ── */}
                    {jobStage === 'customer_sign_off' && (
                      <>
                        {job.customerSignOff && (
                          <button type="button" onClick={onViewSignOff}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold cursor-pointer hover:opacity-80 transition-opacity ${
                              job.customerSignOff.status === 'captured'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-50 text-amber-700'
                            }`}>
                            {job.customerSignOff.status === 'captured'
                              ? <><CheckCircle2 size={13} /> Sign-Off Captured</>
                              : <><AlertTriangle size={13} /> Sign-Off Waived</>}
                          </button>
                        )}
                      </>
                    )}

                    {/* ── Pending Return ── */}
                    {jobStage === 'pending_return' && (() => {
                      const hasBalance = (job.paymentSummary?.outstandingAmountCents ?? 0) > 0;
                      const noTrucksAssigned = (job.assignedTrucks ?? []).length === 0;
                      const destWhId = quote.travelToJob?.destinationWarehouseId;
                      const destWarehouse = destWhId ? warehouses?.find(w => w.id === destWhId) : undefined;
                      const whToDestLeg = destWhId ? quote.warehouseTransit?.[destWhId]?.toDestination : undefined;
                      const warehouseTravelSecs = whToDestLeg?.durationSeconds ?? quote.travelToJob?.destinationTravelSeconds;

                      if (noTrucksAssigned) {
                        return (
                          <>
                            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-stone/60 bg-mist/30 text-[13px] text-slate">
                              <Truck size={14} className="text-slate/60 shrink-0" />
                              <span>No trucks to check in</span>
                            </div>
                            {hasBalance && (
                              <button type="button" onClick={onRequestPayment}
                                className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold flex items-center gap-1.5 self-start hover:bg-emerald-700 active:opacity-80 transition-colors">
                                <DollarSign size={12} /> Request Payment
                              </button>
                            )}
                            <button type="button" onClick={onAdvance} disabled={advancing}
                              className="w-full h-11 rounded-xl bg-plum text-white text-[13px] font-semibold
                                         flex items-center justify-center gap-2
                                         hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors">
                              {advancing ? 'Processing...' : 'Job Complete'}
                            </button>
                          </>
                        );
                      }

                      return (
                        <>
                          {warehouseTravelSecs != null && warehouseTravelSecs > 0 && (
                            <span className="text-[12px] text-slate">
                              ~{formatTransitDuration(warehouseTravelSecs)} from destination
                            </span>
                          )}
                          <PendingReturnTrucks
                            assignedTrucks={job.assignedTrucks ?? []}
                            allTrucks={trucks}
                            warehouses={warehouses}
                            destWarehouse={destWarehouse}
                            dest={dest}
                            onCheckIn={onCheckInTruck}
                          />
                          {hasBalance && (
                            <button type="button" onClick={onRequestPayment}
                              className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold flex items-center gap-1.5 self-start hover:bg-emerald-700 active:opacity-80 transition-colors">
                              <DollarSign size={12} /> Request Payment
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Advance / Revert action bar */}
                  <div className="flex items-center gap-2 mt-3">
                    {stageIdx > 0 && (
                      <button type="button" onClick={onRevert} disabled={reverting}
                        className="w-10 h-10 shrink-0 rounded-lg border border-stone flex items-center justify-center
                                   text-slate hover:text-midnight hover:bg-mist active:bg-stone/60
                                   disabled:opacity-50 transition-colors"
                        aria-label="Revert to previous stage">
                        <Undo2 size={16} />
                      </button>
                    )}
                    {actionLabel && (
                      <button type="button"
                        onClick={() => {
                          if (jobStage === 'customer_sign_off') {
                            onRequestSignOff?.();
                          } else {
                            onAdvance?.();
                          }
                        }}
                        disabled={advancing}
                        className="flex-1 h-11 rounded-xl bg-plum text-white text-[13px] font-semibold
                                   flex items-center justify-center gap-2
                                   hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors">
                        {advancing ? 'Processing...' : actionLabel}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Sign-off badge (clickable to view details) */}
            {job?.customerSignOff && (
              <button type="button" onClick={onViewSignOff}
                className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold cursor-pointer hover:opacity-80 transition-opacity ${
                  job.customerSignOff.status === 'captured'
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-amber-50 text-amber-700'
                }`}>
                {job.customerSignOff.status === 'captured'
                  ? <><CheckCircle2 size={13} /> Customer Sign-Off Captured</>
                  : <><AlertTriangle size={13} /> Sign-Off Waived</>}
              </button>
            )}

            {/* ── Warehouse & Truck cards — scheduled or on_hold ── */}
            {(() => {
              const jobSt = job?.jobStatus ?? quote.jobStatus ?? 'scheduled';
              if (jobSt !== 'scheduled' && jobSt !== 'on_hold') return null;

              const assignedTrucks = job?.assignedTrucks ?? [];
              if (assignedTrucks.length === 0) return null;

              return (
                <div className="flex flex-col gap-2 mt-3">
                  <UncheckedTrucksByWarehouse
                    assignedTrucks={assignedTrucks}
                    allTrucks={trucks}
                    warehouses={warehouses}
                    originWarehouseId={quote.travelToJob?.originWarehouseId}
                  />
                </div>
              );
            })()}

            {/* ── Start Job Button — scheduled + move date is today or past ── */}
            {(() => {
              const isScheduled = (job?.jobStatus ?? quote.jobStatus ?? 'scheduled') === 'scheduled';
              const moveDate = quote.movingDate?.preferredDate;
              const today = new Date().toISOString().slice(0, 10);
              const isMoveDateReady = moveDate ? moveDate <= today : false;
              if (!isScheduled || !isMoveDateReady || !onStartJob) return null;
              return (
                <button
                  type="button"
                  onClick={onStartJob}
                  disabled={startingJob}
                  className="mt-3 w-full h-12 rounded-xl
                             bg-gradient-to-r from-plum to-rose
                             text-white text-[15px] font-bold tracking-wide
                             flex items-center justify-center gap-2.5
                             shadow-lg shadow-plum/30
                             hover:shadow-xl hover:shadow-plum/40
                             active:opacity-90
                             disabled:opacity-50 disabled:cursor-not-allowed
                             transition-all duration-200"
                >
                  {startingJob ? (
                    <LoaderCircle size={18} className="animate-spin" />
                  ) : (
                    <Play size={18} fill="currentColor" />
                  )}
                  {startingJob ? 'Starting...' : 'Start Job'}
                </button>
              );
            })()}

            {/* Quick actions — conditional */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-stone">
              {/* View Crew & Trucks — always */}
              <button type="button" onClick={() => navigate(`/moves/${quote.id}/crew-trucks`)}
                className="flex-1 min-w-[100px] h-9 rounded-lg bg-mist flex items-center justify-center gap-1.5 text-[12px] font-medium text-midnight hover:bg-plum/10 transition-colors">
                <Users size={13} className="text-plum" /> Crew & Trucks
              </button>

              {/* Request Sign-off — only if stage >= pending_return AND no sign-off yet */}
              {job && !job.customerSignOff && job.jobStage &&
                JOB_STAGES.indexOf(job.jobStage) >= JOB_STAGES.indexOf('pending_return') && (
                <button type="button" onClick={() => navigate(`/moves/${quote.id}`)}
                  className="flex-1 min-w-[100px] h-9 rounded-lg bg-mist flex items-center justify-center gap-1.5 text-[12px] font-medium text-midnight hover:bg-plum/10 transition-colors">
                  <FileSignature size={13} className="text-plum" /> Sign-off
                </button>
              )}

              {/* Request Payment — only if nextPaymentDue exists with amountCents > 0 */}
              {job?.paymentSummary?.nextPaymentDue &&
                job.paymentSummary.nextPaymentDue.amountCents > 0 && (
                <button type="button" onClick={() => navigate(`/moves/${quote.id}/payments`)}
                  className="flex-1 min-w-[100px] h-9 rounded-lg bg-mist flex items-center justify-center gap-1.5 text-[12px] font-medium text-midnight hover:bg-plum/10 transition-colors">
                  <DollarSign size={13} className="text-plum" /> Payment
                </button>
              )}

              {/* Log Issue — always */}
              <button type="button" onClick={onLogIssue}
                className="flex-1 min-w-[100px] h-9 rounded-lg bg-mist flex items-center justify-center gap-1.5 text-[12px] font-medium text-midnight hover:bg-plum/10 transition-colors">
                <AlertTriangle size={13} className="text-plum" /> Log Issue
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Hero Truck Card (compact, with detail modal) ─────────────────────────

const HERO_TRUCK_TYPE_LABELS: Record<string, string> = {
  cargo_van: 'Cargo Van',
  sprinter_van: 'Sprinter Van',
  '12ft_box': '12ft Box',
  '16ft_box': '16ft Box',
  '20ft_box': '20ft Box',
  '24ft_box': '24ft Box',
  '26ft_box': '26ft Box',
  flatbed: 'Flatbed',
  other: 'Other',
};

function HeroTruckCard({ truckName, subtitle, truckData, warehouses, onCheckOut, onCheckIn }: {
  truckName: string;
  subtitle: string;
  truckData?: TruckData;
  warehouses?: Warehouse[];
  onCheckOut?: () => void;
  onCheckIn?: () => void;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const warehouseMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of warehouses ?? []) m.set(w.id, w.name);
    return m;
  }, [warehouses]);

  return (
    <>
      <div className="w-full px-3 py-2 rounded-lg border border-stone/60 bg-mist/30">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowDetail(true)}
            className="flex items-center gap-2 flex-1 min-w-0 text-left overflow-hidden">
            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Truck size={13} className="text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-midnight truncate">{truckName}</p>
              <p className="text-[11px] text-slate truncate">{subtitle}</p>
            </div>
          </button>
          {onCheckOut ? (
            <button type="button" disabled={checkingOut}
              onClick={async (e) => {
                e.stopPropagation();
                setCheckingOut(true);
                try { await onCheckOut(); } finally { setCheckingOut(false); }
              }}
              className="w-10 h-10 shrink-0 rounded-lg bg-plum text-white flex items-center justify-center
                         hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors"
              aria-label="Check out truck"
              title="Check Out">
              {checkingOut ? <LoaderCircle size={16} className="animate-spin" /> : <LogOut size={16} />}
            </button>
          ) : onCheckIn ? (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onCheckIn(); }}
              className="w-10 h-10 shrink-0 rounded-lg bg-plum text-white flex items-center justify-center
                         hover:bg-rose active:opacity-80 transition-colors"
              aria-label="Check in truck"
              title="Check In">
              <LogIn size={16} />
            </button>
          ) : (
            <ChevronRight size={14} className="text-slate shrink-0" />
          )}
        </div>
      </div>
      {showDetail && truckData && createPortal(
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
          onClick={() => setShowDetail(false)}>
          <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-[16px] font-semibold text-midnight">{truckData.name}</h3>
              <button type="button" onClick={() => setShowDetail(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:bg-mist transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="flex flex-col gap-2 text-[13px]">
              {truckData.type && <HeroDetailRow label="Type" value={HERO_TRUCK_TYPE_LABELS[truckData.type] ?? truckData.type} />}
              {truckData.year && <HeroDetailRow label="Year" value={String(truckData.year)} />}
              {truckData.make && <HeroDetailRow label="Make" value={truckData.make} />}
              {truckData.model && <HeroDetailRow label="Model" value={truckData.model} />}
              {truckData.licensePlate && <HeroDetailRow label="License Plate" value={truckData.licensePlate} />}
              {truckData.capacity != null && <HeroDetailRow label="Capacity" value={`${truckData.capacity} cu ft`} />}
              {truckData.weightLimit != null && <HeroDetailRow label="Weight Limit" value={`${truckData.weightLimit} lbs`} />}
              {truckData.currentLocation && (
                <HeroDetailRow
                  label="Current Location"
                  value={truckData.currentLocation === 'on_job' ? 'On Job' : (warehouseMap.get(truckData.currentLocation) ?? truckData.currentLocation)}
                />
              )}
              {truckData.status && <HeroDetailRow label="Status" value={truckData.status.charAt(0).toUpperCase() + truckData.status.slice(1)} />}
              {truckData.notes && <HeroDetailRow label="Notes" value={truckData.notes} />}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function HeroDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate shrink-0">{label}</span>
      <span className="text-midnight font-medium text-right">{value}</span>
    </div>
  );
}

/** Render unchecked trucks grouped by warehouse. Shows warehouse card + truck cards with checkout buttons. */
function UncheckedTrucksByWarehouse({ assignedTrucks, allTrucks, warehouses, originWarehouseId, onCheckOut }: {
  assignedTrucks: { truckId: string; truckName: string }[];
  allTrucks?: TruckData[];
  warehouses?: Warehouse[];
  originWarehouseId?: string;
  onCheckOut?: (truckId: string) => Promise<void>;
}) {
  // Build list of unchecked trucks with their data
  const unchecked = assignedTrucks
    .map(at => ({ ...at, data: allTrucks?.find(t => t.id === at.truckId) }))
    .filter(t => t.data?.currentLocation !== 'on_job');

  if (unchecked.length === 0) return null;

  // Build warehouse lookup by ID
  const whById = new Map<string, Warehouse>();
  for (const w of warehouses ?? []) {
    whById.set(w.id, w);
  }

  // Determine the fallback warehouse (origin warehouse)
  const originWh = originWarehouseId ? whById.get(originWarehouseId) : undefined;

  // Group trucks by their current location (warehouse ID)
  // If a truck has no location or its location doesn't match a warehouse, use the origin warehouse
  const groups = new Map<string, typeof unchecked>();
  for (const t of unchecked) {
    const loc = t.data?.currentLocation ?? '';
    let key: string;
    if (loc && whById.has(loc)) {
      key = loc;
    } else if (originWh) {
      key = originWh.id;
    } else {
      key = '';
    }
    const list = groups.get(key) ?? [];
    list.push(t);
    groups.set(key, list);
  }

  return (
    <>
      {[...groups.entries()].map(([warehouseId, groupTrucks]) => {
        const wh = whById.get(warehouseId);
        return (
          <div key={warehouseId || '_unknown'} className="flex flex-col gap-2">
            {wh && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone/60 bg-mist/30">
                <div className="w-7 h-7 rounded-lg bg-plum/10 flex items-center justify-center shrink-0">
                  <WarehouseIcon size={13} className="text-plum" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate">Warehouse</p>
                  <p className="text-[13px] font-medium text-midnight truncate">{wh.name}</p>
                  <p className="text-[11px] text-slate truncate">{wh.address}</p>
                </div>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(wh.address)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="w-7 h-7 shrink-0 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
                  aria-label="Get directions to warehouse">
                  <Navigation size={13} />
                </a>
              </div>
            )}
            {groupTrucks.map(t => {
              const makeModel = t.data ? [t.data.make, t.data.model].filter(Boolean).join(' ') : '';
              const plate = t.data?.licensePlate;
              const sub = [makeModel, plate].filter(Boolean).join(' · ') || 'No details';
              return (
                <HeroTruckCard
                  key={t.truckId}
                  truckName={t.truckName}
                  subtitle={sub}
                  truckData={t.data}
                  warehouses={warehouses}
                  onCheckOut={onCheckOut ? () => onCheckOut(t.truckId) : undefined}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
}

/** Check-in confirmation modal — lets user confirm or change the warehouse. */
function CheckInModal({ isOpen, truckName, defaultWarehouse, warehouses, onConfirm, onClose }: {
  isOpen: boolean;
  truckName: string;
  defaultWarehouse?: Warehouse;
  warehouses?: Warehouse[];
  onConfirm: (warehouseId: string) => Promise<void>;
  onClose: () => void;
}) {
  const [selectedId, setSelectedId] = useState(defaultWarehouse?.id ?? '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelectedId(defaultWarehouse?.id ?? '');
  }, [isOpen, defaultWarehouse?.id]);

  if (!isOpen) return null;

  const selected = (warehouses ?? []).find(w => w.id === selectedId);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
      role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-midnight/70" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-5 flex flex-col gap-4">
        <h3 className="font-display text-[16px] font-semibold text-midnight">Check In Truck</h3>
        <p className="text-[13px] text-slate">
          Confirm that <span className="font-semibold text-midnight">{truckName}</span> is being returned to the warehouse below.
        </p>

        <div>
          <label htmlFor="checkin-warehouse" className="block text-[12px] font-semibold text-midnight mb-1.5">
            Return Warehouse
          </label>
          <select
            id="checkin-warehouse"
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            disabled={submitting}
            className="w-full h-11 px-3 rounded-xl border border-stone text-[13px] text-midnight
                       focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum"
          >
            {(warehouses ?? []).map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 btn-secondary text-[13px]">
            Cancel
          </button>
          <button type="button" disabled={submitting || !selected}
            onClick={async () => {
              if (!selected) return;
              setSubmitting(true);
              try {
                await onConfirm(selected.id);
                onClose();
              } catch {
                // error handled upstream
              } finally {
                setSubmitting(false);
              }
            }}
            className="flex-1 btn-primary text-[13px]">
            {submitting ? 'Checking In...' : 'Confirm Check In'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Render assigned trucks with "Check In" buttons for the pending_return stage. */
function PendingReturnTrucks({ assignedTrucks, allTrucks, warehouses, destWarehouse, dest, onCheckIn }: {
  assignedTrucks: { truckId: string; truckName: string }[];
  allTrucks?: TruckData[];
  warehouses?: Warehouse[];
  destWarehouse?: Warehouse;
  dest?: { address: string };
  onCheckIn?: (truckId: string, warehouseId: string) => Promise<{ stageAdvanced: boolean }>;
}) {
  const [checkInTruck, setCheckInTruck] = useState<{ truckId: string; truckName: string } | null>(null);

  // Separate checked-in vs on-job trucks
  const onJobTrucks = assignedTrucks.filter(at => {
    const td = allTrucks?.find(t => t.id === at.truckId);
    return td?.currentLocation === 'on_job';
  });
  const checkedInTrucks = assignedTrucks.filter(at => {
    const td = allTrucks?.find(t => t.id === at.truckId);
    return td && td.currentLocation !== 'on_job';
  });

  if (onJobTrucks.length === 0 && checkedInTrucks.length === 0) return null;

  return (
    <>
      {/* Destination warehouse card */}
      {destWarehouse && onJobTrucks.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone/60 bg-mist/30">
          <div className="w-7 h-7 rounded-lg bg-plum/10 flex items-center justify-center shrink-0">
            <WarehouseIcon size={13} className="text-plum" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate">Return Warehouse</p>
            <p className="text-[13px] font-medium text-midnight truncate">{destWarehouse.name}</p>
            <p className="text-[11px] text-slate truncate">{destWarehouse.address}</p>
          </div>
          <a href={`https://www.google.com/maps/dir/?api=1${dest ? `&origin=${encodeURIComponent(dest.address)}` : ''}&destination=${encodeURIComponent(destWarehouse.address)}`}
            target="_blank" rel="noopener noreferrer"
            className="w-7 h-7 shrink-0 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
            aria-label="Get directions to warehouse">
            <Navigation size={13} />
          </a>
        </div>
      )}

      {/* Trucks still on job (need check-in) */}
      {onJobTrucks.map(at => {
        const td = allTrucks?.find(t => t.id === at.truckId);
        const makeModel = td ? [td.make, td.model].filter(Boolean).join(' ') : '';
        const plate = td?.licensePlate;
        const sub = [makeModel, plate].filter(Boolean).join(' · ') || 'No details';
        return (
          <HeroTruckCard
            key={at.truckId}
            truckName={at.truckName}
            subtitle={sub}
            truckData={td}
            warehouses={warehouses}
            onCheckIn={onCheckIn ? () => setCheckInTruck(at) : undefined}
          />
        );
      })}

      {/* Trucks already checked in */}
      {checkedInTrucks.length > 0 && (
        <div className="flex flex-col gap-1">
          {checkedInTrucks.map(at => {
            const td = allTrucks?.find(t => t.id === at.truckId);
            const locName = td?.currentLocation
              ? (warehouses ?? []).find(w => w.id === td.currentLocation)?.name ?? td.currentLocation
              : '';
            return (
              <div key={at.truckId} className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-slate">
                <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                <span className="truncate">{at.truckName}</span>
                <span className="text-[11px]">— {locName}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Check-in confirmation modal */}
      <CheckInModal
        isOpen={!!checkInTruck}
        truckName={checkInTruck?.truckName ?? ''}
        defaultWarehouse={destWarehouse}
        warehouses={warehouses}
        onConfirm={async (warehouseId) => {
          if (!checkInTruck || !onCheckIn) return;
          await onCheckIn(checkInTruck.truckId, warehouseId);
          setCheckInTruck(null);
        }}
        onClose={() => setCheckInTruck(null)}
      />
    </>
  );
}

// ── Estimate Scheduled Sheet ──────────────────────────────────────────────

export function EstimateScheduledSheet({
  users,
  usersLoading,
  onConfirm,
  onClose,
}: {
  users: OrgUser[];
  usersLoading: boolean;
  onConfirm: (estimateDate: string, estimatorUid: string, estimatorName: string) => void;
  onClose: () => void;
}) {
  const [estimateDateTime, setEstimateDateTime] = useState('');
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUser = users.find(u => u.uid === selectedUid);

  const eligibleUsers = useMemo(() =>
    users.filter(u => u.status === 'active' && (u.role === 'estimator' || u.role === 'admin' || u.role === 'field_lead')),
    [users],
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[85vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight">Schedule Estimate</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"><X size={16} /></button>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-widest text-slate">Estimate Visit Date & Time</label>
            <DateTimePicker value={estimateDateTime} onChange={setEstimateDateTime} placeholder="Set estimate date" />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate">
              Quote Lead
            </label>
            {usersLoading ? (
              <div className="flex justify-center py-6"><LoadingSpinner size="sm" /></div>
            ) : (
              <ul className="flex flex-col gap-2 max-h-48 overflow-y-auto">
                {eligibleUsers.map(u => {
                  const isActive = u.uid === selectedUid;
                  const name = u.displayName || u.email;
                  return (
                    <li key={u.uid}>
                      <button type="button" onClick={() => setSelectedUid(u.uid)}
                        className={`w-full flex items-center gap-3 min-h-[44px] px-4 py-2 rounded-xl border text-left transition-all duration-150
                          ${isActive ? 'border-plum bg-plum/5' : 'border-stone bg-white hover:border-plum/30'}`}>
                        <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold uppercase
                          ${isActive ? 'bg-plum text-white' : 'bg-mist text-slate'}`}>
                          {name[0]?.toUpperCase() ?? '?'}
                        </div>
                        <span className={`text-small font-medium flex-1 truncate ${isActive ? 'text-plum' : 'text-midnight'}`}>
                          {name}
                        </span>
                        {isActive && <Check size={14} className="shrink-0 text-plum" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <button type="button"
            onClick={() => {
              onConfirm(estimateDateTime, selectedUid, selectedUser?.displayName || selectedUser?.email || '');
            }}
            className="w-full h-12 rounded-xl bg-plum text-white font-semibold text-small
                       hover:bg-plum/90 active:bg-plum/80 transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed mt-1">
            Convert to Quote
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Activity Log Sheet ───────────────────────────────────────────────────

export function CommunicationLogSheet({
  quote,
  updateQuote,
  onClose,
}: {
  quote: Quote;
  updateQuote: (patch: QuotePatch) => Promise<unknown>;
  onClose: () => void;
}) {
  const { currentUser } = useUserProfile();
  const [activityType, setActivityType] = useState<LeadCommunicationType>('phone_call');
  const [note, setNote] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState(getDefaultFollowUp);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) { setError('Note is required.'); return; }
    setError('');
    setSaving(true);
    try {
      const newEntry: LeadCommunicationEntry = {
        id: crypto.randomUUID(),
        type: activityType,
        note: note.trim(),
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.uid ?? '',
        createdByName: currentUser?.displayName ?? currentUser?.email ?? 'Unknown',
      };
      const existing = quote.leadCommunicationLog ?? [];
      await updateQuote({
        leadCommunicationLog: [...existing, newEntry],
        ...(nextFollowUp ? { nextFollowUp } : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add activity');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[85vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight">Log Communication</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
              <p className="text-[12px] text-rose">{error}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5">
            {ALL_COMMUNICATION_TYPES.map(t => {
              const Icon = COMMUNICATION_TYPE_ICON[t];
              const active = activityType === t;
              return (
                <button key={t} type="button" onClick={() => setActivityType(t)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-150
                    ${active ? 'bg-plum text-white' : 'bg-mist text-slate hover:text-midnight'}`}>
                  <Icon size={12} />
                  {LEAD_COMMUNICATION_TYPE_LABEL[t]}
                </button>
              );
            })}
          </div>

          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="What happened? Describe the interaction…" rows={3}
            className="w-full rounded-xl border border-stone bg-white px-4 py-3 text-small text-midnight
                       placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                       focus:border-plum resize-none" />

          <DateInput
            label="Next Follow-Up"
            value={nextFollowUp}
            onChange={setNextFollowUp}
            min={getTodayStr()}
          />

          <button type="submit" disabled={saving || !note.trim()}
            className="h-11 rounded-xl bg-plum text-white text-small font-semibold flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150">
            {saving ? <LoadingSpinner size="sm" /> : null}
            {saving ? 'Adding…' : 'Add Entry'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── (LifecycleProgressBar removed — single unified view per stage) ────────

// ── Dashboard Cards ────────────────────────────────────────────────────────

function DashCard({ title, icon, children, linkTo, linkLabel }: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-stone">
        <span className="text-plum">{icon}</span>
        <span className="text-[12px] font-bold uppercase tracking-widest text-slate flex-1">{title}</span>
        {linkTo && (
          <Link to={linkTo} className="text-[11px] text-plum hover:underline flex items-center gap-0.5">
            {linkLabel ?? 'View'} <ChevronRight size={12} />
          </Link>
        )}
      </div>
      <div className="px-4 py-3">
        {children}
      </div>
    </div>
  );
}

// ── Individual Dashboard Cards ─────────────────────────────────────────────

function ContactCard({ quote }: { quote: Quote }) {
  const poc = quote.pointOfContact;
  return (
    <DashCard title="Contact" icon={<User2 size={14} />}>
      <div className="divide-y divide-stone">
        <DataRow label="Name" value={poc?.name} />
        <DataRow label="Phone" value={poc?.phone ? (
          <a href={`tel:${poc.phone}`} className="text-plum hover:underline">{poc.phone}</a>
        ) : null} />
        <DataRow label="Email" value={poc?.email ? (
          <a href={`mailto:${poc.email}`} className="text-plum hover:underline">{poc.email}</a>
        ) : null} />
      </div>
    </DashCard>
  );
}

function CompanyCard({ quote }: { quote: Quote }) {
  if (quote.type !== 'commercial') return null;
  return (
    <DashCard title="Company" icon={<Building2 size={14} />}>
      <div className="divide-y divide-stone">
        <DataRow label="Company" value={quote.customerCompanyName} />
        <DataRow label="Employees" value={quote.numberOfEmployees} />
        <DataRow label="Square Footage" value={quote.squareFootage?.toLocaleString()} />
      </div>
    </DashCard>
  );
}

function PropertyCard({ quote }: { quote: Quote }) {
  if (quote.type !== 'residential') return null;
  return (
    <DashCard title="Property" icon={<Home size={14} />}>
      <div className="divide-y divide-stone">
        <DataRow label="Bedrooms" value={quote.numberOfBedrooms} />
        <DataRow label="Square Footage" value={quote.squareFootage?.toLocaleString()} />
        <DataRow label="Stories (Origin)" value={quote.originStories} />
        <DataRow label="Stories (Dest)" value={quote.destinationStories} />
        <DataRow label="Elevator (Origin)" value={quote.originElevatorAccess ? 'Yes' : 'No'} />
        <DataRow label="Elevator (Dest)" value={quote.destinationElevatorAccess ? 'Yes' : 'No'} />
      </div>
    </DashCard>
  );
}

function AddressCard({ quote }: { quote: Quote }) {
  const transit = quote.addresses?.transit;
  return (
    <DashCard title="Route" icon={<MapPin size={14} />}>
      <div className="flex flex-col gap-2">
        {quote.addresses?.origin?.address && (
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
            </span>
            <span className="text-[12px] text-midnight leading-snug">{quote.addresses.origin.address}</span>
          </div>
        )}
        {!quote.addresses?.origin?.address && quote.originZipCode && (
          <DataRow label="Origin Zip" value={quote.originZipCode} />
        )}
        {quote.addresses?.destination?.address && (
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-rose/20 flex items-center justify-center shrink-0 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-rose" />
            </span>
            <span className="text-[12px] text-midnight leading-snug">{quote.addresses.destination.address}</span>
          </div>
        )}
        {!quote.addresses?.destination?.address && quote.destinationZipCode && (
          <DataRow label="Dest Zip" value={quote.destinationZipCode} />
        )}
        {transit && (
          <div className="flex items-center gap-4 text-[12px] text-midnight pt-2 border-t border-stone">
            <span className="flex items-center gap-1.5">
              <Navigation size={12} className="text-plum" /> {formatDistance(transit.distanceMeters)}
            </span>
            <span className="flex items-center gap-1.5">
              <ArrowRight size={12} className="text-plum" /> {formatDuration(transit.durationSeconds)}
            </span>
          </div>
        )}
      </div>
    </DashCard>
  );
}

function MoveDatesCard({ quote }: { quote: Quote }) {
  return (
    <DashCard title="Move Dates" icon={<Calendar size={14} />}>
      <div className="divide-y divide-stone">
        <DataRow label="Date Range" value={quote.estimatedMoveDateRange} />
        <DataRow label="Preferred Date" value={
          quote.movingDate?.preferredDate
            ? new Date(quote.movingDate.preferredDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : null
        } />
        <DataRow label="Deadline" value={
          quote.movingDate?.deadline
            ? new Date(quote.movingDate.deadline + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : null
        } />
        <DataRow label="Move Type" value={quote.moveType} />
      </div>
    </DashCard>
  );
}

function LeadInfoCard({ quote }: { quote: Quote }) {
  return (
    <DashCard title="Lead Info" icon={<ClipboardList size={14} />}>
      <div className="divide-y divide-stone">
        {quote.leadStatus && (
          <DataRow label="Status" value={
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${LEAD_STATUS_CLASSES[quote.leadStatus]}`}>
              {LEAD_STATUS_LABEL[quote.leadStatus]}
            </span>
          } />
        )}
        <DataRow label="Source" value={quote.discoverySource?.replace(/_/g, ' ')} />
        <DataRow label="Channel" value={quote.inboundChannel?.replace(/_/g, ' ')} />
        <DataRow label="Follow-Up" value={
          quote.nextFollowUp
            ? <span className={new Date(quote.nextFollowUp) < new Date() ? 'text-rose font-semibold' : 'text-plum'}>
                {new Date(quote.nextFollowUp + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            : null
        } />
        <DataRow label="Estimated Value" value={
          quote.estimatedValue != null && quote.estimatedValue > 0
            ? <span className="text-emerald-700 font-semibold">{quote.estimatedValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}</span>
            : null
        } />
        {quote.leadLostReason && <DataRow label="Lost Reason" value={<span className="text-rose">{quote.leadLostReason}</span>} />}
      </div>
    </DashCard>
  );
}

function ActivityCard({ quote }: { quote: Quote }) {
  const entries = useMemo(() => {
    const log = quote.leadCommunicationLog ?? [];
    return [...log].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [quote.leadCommunicationLog]);

  return (
    <DashCard title={`Activity (${entries.length})`} icon={<MessageSquare size={14} />}>
      {entries.length === 0 ? (
        <p className="text-[13px] text-slate italic">No communications logged yet</p>
      ) : (
        <div className="flex flex-col gap-2">
          {entries.slice(0, 3).map(entry => (
            <div key={entry.id} className="bg-mist rounded-lg px-3 py-2">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-plum">
                  {entry.type.replace(/_/g, ' ')}
                </span>
                <span className="text-[10px] text-slate">
                  {new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
              <p className="text-[12px] text-midnight leading-snug line-clamp-2">{entry.note}</p>
            </div>
          ))}
          {entries.length > 3 && (
            <p className="text-[11px] text-slate text-center">+ {entries.length - 3} more entries</p>
          )}
        </div>
      )}
    </DashCard>
  );
}

function FormulaCard({ quote }: { quote: Quote }) {
  const formula = quote.formula;
  return (
    <DashCard title="Formula & Pricing" icon={<FlaskConical size={14} />} linkTo={`/moves/${quote.id}/formula`} linkLabel="Details">
      {!formula ? (
        <p className="text-[13px] text-slate italic">Formula not yet run</p>
      ) : (
        <div className="divide-y divide-stone">
          <DataRow label="Crew" value={formula.finalCrewSize} />
          <DataRow label="Trucks" value={formula.truckQuantity} />
          <DataRow label="Hours" value={formula.totalHours} />
          <DataRow label="Total" value={
            formula.finalCostEstimate != null ? (
              <span className="text-emerald-700 font-semibold text-[14px]">
                {formula.finalCostEstimate.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </span>
            ) : null
          } />
        </div>
      )}
    </DashCard>
  );
}

function InventoryCard({ quote }: { quote: Quote }) {
  const casegoods = quote.casegoodsInventory ?? [];
  const rooms = quote.residentialRooms ?? [];
  const totalItems = casegoods.length + rooms.reduce((sum, r) => sum + (r.items?.length ?? 0), 0);

  return (
    <DashCard title={`Inventory (${totalItems} items)`} icon={<Box size={14} />}
      linkTo={quote.type === 'commercial' ? `/moves/${quote.id}/casegoods` : `/moves/${quote.id}/residential-rooms`}
      linkLabel="Edit">
      {casegoods.length === 0 && rooms.length === 0 ? (
        <p className="text-[13px] text-slate italic">No inventory recorded</p>
      ) : (
        <div>
          {casegoods.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate mb-1">Casegoods</p>
              {casegoods.slice(0, 4).map((item, i) => (
                <div key={i} className="flex justify-between text-[12px] py-0.5">
                  <span className="text-midnight truncate">{item.itemName}</span>
                  <span className="text-slate font-mono ml-2">{item.quantity ?? 1}</span>
                </div>
              ))}
              {casegoods.length > 4 && <p className="text-[10px] text-slate">+ {casegoods.length - 4} more</p>}
            </div>
          )}
          {rooms.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate mb-1">Rooms ({rooms.length})</p>
              {rooms.slice(0, 3).map((room, i) => (
                <p key={i} className="text-[12px] text-midnight py-0.5">
                  {room.roomName} — {(room.items ?? []).length} items
                </p>
              ))}
              {rooms.length > 3 && <p className="text-[10px] text-slate">+ {rooms.length - 3} more</p>}
            </div>
          )}
        </div>
      )}
    </DashCard>
  );
}

function CrewCard({ quote }: { quote: Quote }) {
  const crew = quote.crew ?? [];
  return (
    <DashCard title={`Crew (${crew.length})`} icon={<Users size={14} />}>
      {crew.length === 0 ? (
        <p className="text-[13px] text-slate italic">No crew assigned yet</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {crew.map((member, i) => (
            <div key={i} className="flex items-center gap-2 bg-mist rounded-lg px-2.5 py-1.5">
              <div className="w-6 h-6 rounded-full bg-plum/10 flex items-center justify-center text-[10px] font-bold text-plum uppercase">
                {(member.name || '?')[0]}
              </div>
              <div>
                <p className="text-[11px] font-medium text-midnight">{member.name}</p>
                {member.slug && <p className="text-[9px] text-slate capitalize">{member.slug}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashCard>
  );
}

function NotesCard({ quote }: { quote: Quote }) {
  if (!quote.notes) return null;
  return (
    <DashCard title="Notes" icon={<ClipboardList size={14} />}>
      <p className="text-[13px] text-midnight whitespace-pre-wrap line-clamp-6">{quote.notes}</p>
    </DashCard>
  );
}

// ── Editable Lead Detail Cards ────────────────────────────────────────────

export function EditableCustomerCard({ quote, save }: { quote: Quote; save: (p: QuotePatch) => void }) {
  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Customer</p>
      <div className="flex flex-col gap-3">
        <FieldRow label="Customer Name">
          <TextFieldEdit value={quote.pointOfContact?.name ?? ''} placeholder="Customer name"
            onChange={v => save({ pointOfContact: { ...quote.pointOfContact, name: v } })} />
        </FieldRow>

        <FieldRow label="Phone">
          <div className="flex items-center gap-2">
            <TextFieldEdit value={quote.pointOfContact?.phone ?? ''} placeholder="(555) 123-4567" type="tel" inputMode="tel"
              onChange={v => save({ pointOfContact: { ...quote.pointOfContact, phone: v } })} />
            {quote.pointOfContact?.phone && (
              <div className="flex gap-1 shrink-0">
                <a href={`tel:${quote.pointOfContact.phone}`}
                  className="w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
                  aria-label="Call"><Phone size={16} /></a>
                <a href={`sms:${quote.pointOfContact.phone}`}
                  className="w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors"
                  aria-label="Text"><MessageSquare size={16} /></a>
              </div>
            )}
          </div>
        </FieldRow>

        <FieldRow label="Email">
          <div className="flex items-center gap-2">
            <TextFieldEdit value={quote.pointOfContact?.email ?? ''} placeholder="john@example.com" type="email" inputMode="email"
              onChange={v => save({ pointOfContact: { ...quote.pointOfContact, email: v } })} />
            {quote.pointOfContact?.email && (
              <a href={`mailto:${quote.pointOfContact.email}`}
                className="w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center text-plum hover:bg-plum/20 transition-colors shrink-0"
                aria-label="Email"><Mail size={16} /></a>
            )}
          </div>
        </FieldRow>
      </div>
    </div>
  );
}

export function EditableCompanyCard({ quote, save, org }: { quote: Quote; save: (p: QuotePatch) => void; org: Organization | null }) {
  if (quote.type !== 'commercial') return null;
  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
      <div className="flex items-center gap-2 mb-3">
        <Building2 size={14} className="text-slate" />
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Company</p>
      </div>
      <div className="flex flex-col gap-3">
        <FieldRow label="Company Name">
          <TextFieldEdit value={quote.customerCompanyName ?? ''} placeholder="Company name"
            onChange={v => save({ customerCompanyName: v || '' })} />
        </FieldRow>
        <FieldRow label="Number of Employees">
          <TextFieldEdit value={quote.numberOfEmployees?.toString() ?? ''} placeholder="e.g. 50"
            type="number" inputMode="numeric"
            onChange={v => save({ numberOfEmployees: v ? parseInt(v, 10) : undefined })} />
        </FieldRow>
        {org?.commercialBallparkEnabled && org.commercialBallparkMethod === 'per_sqft' && (
          <FieldRow label="Square Footage">
            <TextFieldEdit value={quote.squareFootage?.toString() ?? ''} placeholder="e.g. 5000"
              type="number" inputMode="numeric"
              onChange={v => save({ squareFootage: v ? parseInt(v, 10) : undefined })} />
          </FieldRow>
        )}
      </div>
    </div>
  );
}

export function EditableLeadInfoCard({ quote, save }: { quote: Quote; save: (p: QuotePatch) => void }) {
  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Lead Info</p>
      <div className="flex flex-col gap-3">
        <FieldRow label="How They Found Us">
          <CustomDropdown
            options={DISCOVERY_SOURCE_OPTIONS}
            value={quote.discoverySource ?? ''}
            onChange={v => save({ discoverySource: (v || undefined) as DiscoverySource | undefined })}
            placeholder="Select a source…"
          />
        </FieldRow>

        {(quote.discoverySource === 'referral' || (!quote.discoverySource && quote.leadSource === 'referral')) && (
          <FieldRow label="Referred By">
            <TextFieldEdit value={quote.referredBy ?? ''} placeholder="Who referred this lead?"
              onChange={v => save({ referredBy: v })} />
          </FieldRow>
        )}

        <FieldRow label="Inbound Channel">
          <CustomDropdown
            options={INBOUND_CHANNEL_OPTIONS}
            value={quote.inboundChannel ?? ''}
            onChange={v => save({ inboundChannel: (v || undefined) as InboundChannel | undefined })}
            placeholder="Select a channel…"
          />
        </FieldRow>

        <FieldRow label="Created">
          <p className="text-small text-slate">
            {quote.createdAt ? new Date(quote.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
            {quote.createdByName && <span className="ml-1">by {quote.createdByName}</span>}
          </p>
        </FieldRow>
      </div>
    </div>
  );
}

export function EditableMoveInfoCard({ quote, save }: { quote: Quote; save: (p: QuotePatch) => void }) {
  const todayStr = getTodayStr();
  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Move Info</p>
      <div className="flex flex-col gap-3">
        <FieldRow label="Estimated Move Date Range">
          <TextFieldEdit value={quote.estimatedMoveDateRange ?? ''} placeholder='e.g. "June" or "last week of October"'
            onChange={v => save({ estimatedMoveDateRange: v || undefined })} />
        </FieldRow>

        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold uppercase tracking-widest text-slate">Preferred Move Date</label>
          <DateTimePicker
            value={quote.movingDate?.preferredDate ?? ''}
            onChange={v => save({ movingDate: { ...quote.movingDate, preferredDate: v || undefined } })}
            placeholder="Set move date"
          />
        </div>

        <DateInput
          label="Move Deadline"
          value={quote.movingDate?.deadline ?? ''}
          onChange={v => save({ movingDate: { ...quote.movingDate, deadline: v || undefined } })}
          min={todayStr}
        />
      </div>
    </div>
  );
}

export function EditableAddressesCard({ quote, save, calculatingTransit, transit, org, warehouseDistance }: {
  quote: Quote;
  save: (p: QuotePatch) => void;
  calculatingTransit: boolean;
  transit: { distanceMeters: number; durationSeconds: number } | null;
  org: Organization | null;
  warehouseDistance?: {
    loading: boolean;
    name: string | null;
    distanceMeters: number | null;
    durationSeconds: number | null;
  };
}) {
  const originPlace: SelectedPlace | null = quote.addresses?.origin?.address
    ? { address: quote.addresses.origin.address, placeId: quote.addresses.origin.placeId ?? '' }
    : null;
  const destPlace: SelectedPlace | null = quote.addresses?.destination?.address
    ? { address: quote.addresses.destination.address, placeId: quote.addresses.destination.placeId ?? '' }
    : null;

  function handleOriginChange(place: SelectedPlace | null) {
    save({
      addresses: {
        ...quote.addresses,
        origin: place ? { address: place.address, placeId: place.placeId } : undefined,
      },
    });
  }

  function handleDestChange(place: SelectedPlace | null) {
    save({
      addresses: {
        ...quote.addresses,
        destination: place ? { address: place.address, placeId: place.placeId } : undefined,
      },
    });
  }

  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Addresses</p>
      <div className="flex flex-col gap-3">
        <AddressSearch
          label="Origin Address"
          id="unified-origin-address"
          value={originPlace}
          onChange={handleOriginChange}
        />
        <FieldRow label="Origin Zip Code">
          <TextFieldEdit value={quote.originZipCode ?? ''} placeholder="e.g. 10001"
            onChange={v => save({ originZipCode: v || undefined })} />
        </FieldRow>
        <AddressSearch
          label="Destination Address"
          id="unified-dest-address"
          value={destPlace}
          onChange={handleDestChange}
        />
        <FieldRow label="Destination Zip Code">
          <TextFieldEdit value={quote.destinationZipCode ?? ''} placeholder="e.g. 90210"
            onChange={v => save({ destinationZipCode: v || undefined })} />
        </FieldRow>
      </div>

      {/* Transit display */}
      {(calculatingTransit || transit) && (
        <div className="mt-3 pt-3 border-t border-stone">
          {calculatingTransit ? (
            <div className="flex items-center gap-2 text-small text-slate">
              <LoadingSpinner size="sm" /> Calculating route…
            </div>
          ) : transit ? (
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg bg-mist px-3 py-2.5 flex items-center gap-2">
                <Navigation size={14} className="text-plum shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate font-bold leading-none mb-0.5">Distance</p>
                  <p className="text-[13px] font-semibold text-midnight leading-tight">{formatDistance(transit.distanceMeters, org?.defaultUnits)}</p>
                </div>
              </div>
              <div className="flex-1 rounded-lg bg-mist px-3 py-2.5 flex items-center gap-2">
                <Clock size={14} className="text-plum shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate font-bold leading-none mb-0.5">Drive Time</p>
                  <p className="text-[13px] font-semibold text-midnight leading-tight">{formatDuration(transit.durationSeconds)}</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Move Type */}
      <div className="mt-3 pt-3 border-t border-stone">
        <FieldRow label="Move Type">
          <CustomDropdown
            options={MOVE_TYPE_OPTIONS}
            value={quote.moveType ?? ''}
            onChange={v => save({ moveType: (v || undefined) as 'local' | 'intrastate' | 'interstate' | undefined })}
            placeholder="Select…"
          />
        </FieldRow>
      </div>

      {/* Nearest warehouse distance */}
      {warehouseDistance && (warehouseDistance.loading || warehouseDistance.name) && (
        <div className="mt-3 pt-3 border-t border-stone">
          <p className="text-[10px] uppercase tracking-wider text-slate font-bold mb-2">Nearest Warehouse</p>
          {warehouseDistance.loading ? (
            <div className="flex items-center gap-2 text-small text-slate">
              <LoadingSpinner size="sm" /> Calculating…
            </div>
          ) : warehouseDistance.name && warehouseDistance.distanceMeters != null && warehouseDistance.durationSeconds != null ? (
            <div className="rounded-lg bg-mist px-3 py-2.5">
              <p className="text-[13px] font-semibold text-midnight">{warehouseDistance.name}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-[12px] text-slate">
                  <Navigation size={12} className="text-plum" />
                  {formatDistance(warehouseDistance.distanceMeters, org?.defaultUnits)}
                </span>
                <span className="flex items-center gap-1 text-[12px] text-slate">
                  <Clock size={12} className="text-plum" />
                  {formatDuration(warehouseDistance.durationSeconds)}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ── RentCast mapping (matches MoveAccessPropertyPage) ────────────────────────

function mapRentCastToQuotePatch(data: RentCastPropertyData): Partial<QuotePatch> {
  const patch: Partial<QuotePatch> = {};
  if (data.bedrooms != null) patch.numberOfBedrooms = data.bedrooms;
  if (data.squareFootage != null) patch.squareFootage = data.squareFootage;

  const f = data.features as Record<string, unknown> | undefined;
  if (f) {
    if (typeof f.floorCount === 'number' && f.floorCount > 0) patch.originStories = f.floorCount;
  }
  return patch;
}

export function EditablePropertyDetailsCard({ quote, save, orgId }: { quote: Quote; save: (p: QuotePatch) => void; orgId: string | null }) {
  if (quote.type !== 'residential') return null;

  /* eslint-disable react-hooks/rules-of-hooks */
  const [smartFillLoading, setSmartFillLoading] = useState(false);
  const [smartFillError, setSmartFillError] = useState<string | null>(null);
  const [importedData, setImportedData] = useState<RentCastPropertyData | null>(
    quote.addresses?.origin?.importedPropertyData ?? null,
  );
  const [showImported, setShowImported] = useState(false);
  /* eslint-enable react-hooks/rules-of-hooks */

  const originAddress = quote.addresses?.origin?.address;
  const originLongCarry = quote.addresses?.origin?.residentialProperty?.longCarry ?? false;
  const destLongCarry = quote.addresses?.destination?.residentialProperty?.longCarry ?? false;

  async function handleSmartFill() {
    if (!originAddress || !orgId) return;
    setSmartFillLoading(true);
    setSmartFillError(null);
    try {
      const res = await api.get<{ success: boolean; data: RentCastPropertyData | null }>(
        `/quotes/${orgId}/property-lookup?address=${encodeURIComponent(originAddress)}`,
      );
      const data = res.data;
      if (!data) {
        setSmartFillError('No property data found for this address.');
        setSmartFillLoading(false);
        return;
      }
      // Map to top-level quote fields
      const patch = mapRentCastToQuotePatch(data);
      // Also store imported data + mapped residential property on address
      const resProp = quote.addresses?.origin?.residentialProperty ?? {};
      if (data.bedrooms != null) resProp.bedrooms = data.bedrooms;
      if (data.squareFootage != null) resProp.squareFootage = data.squareFootage;
      const f = data.features as Record<string, unknown> | undefined;
      if (f && typeof f.floorCount === 'number' && f.floorCount > 0) resProp.stories = f.floorCount;

      save({
        ...patch,
        addresses: {
          ...quote.addresses,
          origin: {
            ...quote.addresses?.origin,
            address: quote.addresses?.origin?.address ?? '',
            placeId: quote.addresses?.origin?.placeId ?? '',
            residentialProperty: resProp,
            importedPropertyData: data,
          },
        },
      });
      setImportedData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not fetch property details.';
      setSmartFillError(message);
    }
    setSmartFillLoading(false);
  }

  function saveOriginLongCarry(v: boolean) {
    save({
      addresses: {
        ...quote.addresses,
        origin: {
          ...quote.addresses?.origin,
          address: quote.addresses?.origin?.address ?? '',
          placeId: quote.addresses?.origin?.placeId ?? '',
          residentialProperty: {
            ...quote.addresses?.origin?.residentialProperty,
            longCarry: v,
          },
        },
      },
    });
  }

  function saveDestLongCarry(v: boolean) {
    save({
      addresses: {
        ...quote.addresses,
        destination: {
          ...quote.addresses?.destination,
          address: quote.addresses?.destination?.address ?? '',
          placeId: quote.addresses?.destination?.placeId ?? '',
          residentialProperty: {
            ...quote.addresses?.destination?.residentialProperty,
            longCarry: v,
          },
        },
      },
    });
  }

  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Property Details</p>
        {originAddress && (
          <button
            type="button"
            onClick={handleSmartFill}
            disabled={smartFillLoading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold
                       text-plum bg-plum/5 border border-plum/20
                       hover:bg-plum/10 active:bg-plum/15
                       transition-colors duration-150 disabled:opacity-50"
            title="Auto-fill property details from origin address"
            aria-label="Smart fill origin property details"
          >
            {smartFillLoading
              ? <LoaderCircle size={13} className="animate-spin" />
              : <Sparkles size={13} />
            }
            Smart Fill
          </button>
        )}
      </div>

      {smartFillError && (
        <p className="text-[12px] text-rose mb-3">{smartFillError}</p>
      )}

      {/* Origin subsection */}
      <p className="text-[10px] font-bold uppercase tracking-widest text-plum mb-2 mt-1">Origin</p>
      <div className="flex flex-col gap-3 mb-4">
        <FieldRow label="Number of Bedrooms">
          <TextFieldEdit value={quote.numberOfBedrooms?.toString() ?? ''} placeholder="e.g. 3"
            type="number" inputMode="numeric"
            onChange={v => save({ numberOfBedrooms: v ? parseInt(v, 10) : undefined })} />
        </FieldRow>
        <FieldRow label="Square Footage">
          <TextFieldEdit value={quote.squareFootage?.toString() ?? ''} placeholder="e.g. 2000"
            type="number" inputMode="numeric"
            onChange={v => save({ squareFootage: v ? parseInt(v, 10) : undefined })} />
        </FieldRow>
        <FieldRow label="Stories">
          <TextFieldEdit value={quote.originStories?.toString() ?? ''} placeholder="e.g. 2"
            type="number" inputMode="numeric"
            onChange={v => save({ originStories: v ? parseInt(v, 10) : undefined })} />
        </FieldRow>
        <ToggleField label="Elevator Access" value={quote.originElevatorAccess ?? false}
          onChange={v => save({ originElevatorAccess: v })} />
        <ToggleField label="Long Carry" value={originLongCarry}
          onChange={saveOriginLongCarry} />
        {importedData && (
          <button
            type="button"
            onClick={() => setShowImported(true)}
            className="text-[12px] text-plum hover:underline text-left"
          >
            View Imported Property Details
          </button>
        )}
      </div>

      {/* Destination subsection */}
      <div className="border-t border-stone pt-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-plum mb-2">Destination</p>
        <div className="flex flex-col gap-3">
          <FieldRow label="Stories">
            <TextFieldEdit value={quote.destinationStories?.toString() ?? ''} placeholder="e.g. 1"
              type="number" inputMode="numeric"
              onChange={v => save({ destinationStories: v ? parseInt(v, 10) : undefined })} />
          </FieldRow>
          <ToggleField label="Elevator Access" value={quote.destinationElevatorAccess ?? false}
            onChange={v => save({ destinationElevatorAccess: v })} />
          <ToggleField label="Long Carry" value={destLongCarry}
            onChange={saveDestLongCarry} />
        </div>
      </div>

      {/* Imported Property Details Modal */}
      {showImported && importedData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4"
          onClick={() => setShowImported(false)}>
          <div className="bg-white rounded-xl shadow-brand-lg w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone">
              <p className="font-semibold text-small text-midnight">Imported Property Details</p>
              <button type="button" onClick={() => setShowImported(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight
                           hover:bg-mist transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-4 flex flex-col gap-2 text-[13px] text-midnight">
              {importedData.formattedAddress && (
                <div className="flex justify-between"><span className="text-slate">Address</span><span className="text-right">{importedData.formattedAddress}</span></div>
              )}
              {importedData.propertyType && (
                <div className="flex justify-between"><span className="text-slate">Property Type</span><span>{importedData.propertyType}</span></div>
              )}
              {importedData.bedrooms != null && (
                <div className="flex justify-between"><span className="text-slate">Bedrooms</span><span>{importedData.bedrooms}</span></div>
              )}
              {importedData.bathrooms != null && (
                <div className="flex justify-between"><span className="text-slate">Bathrooms</span><span>{importedData.bathrooms}</span></div>
              )}
              {importedData.squareFootage != null && (
                <div className="flex justify-between"><span className="text-slate">Square Footage</span><span>{importedData.squareFootage.toLocaleString()}</span></div>
              )}
              {importedData.lotSize != null && (
                <div className="flex justify-between"><span className="text-slate">Lot Size</span><span>{importedData.lotSize.toLocaleString()}</span></div>
              )}
              {importedData.yearBuilt != null && (
                <div className="flex justify-between"><span className="text-slate">Year Built</span><span>{importedData.yearBuilt}</span></div>
              )}
              {importedData.county && (
                <div className="flex justify-between"><span className="text-slate">County</span><span>{importedData.county}</span></div>
              )}
              {importedData.ownerNames && importedData.ownerNames.length > 0 && (
                <div className="flex justify-between"><span className="text-slate">Owner</span><span className="text-right">{importedData.ownerNames.join(', ')}</span></div>
              )}
              {importedData.features && Object.keys(importedData.features).length > 0 && (
                <>
                  <div className="border-t border-stone mt-1 pt-2">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Features</p>
                  </div>
                  {Object.entries(importedData.features).map(([key, val]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-slate">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}</span>
                      <span>{val === true ? 'Yes' : val === false ? 'No' : typeof val === 'number' ? val.toLocaleString() : String(val ?? '—')}</span>
                    </div>
                  ))}
                </>
              )}
              <p className="text-[11px] text-slate italic mt-2">Data provided by RentCast</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EditableNotesCard({ quote, save }: { quote: Quote; save: (p: QuotePatch) => void }) {
  return (
    <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Notes</p>
      <NotesField value={quote.notes ?? ''} onChange={v => save({ notes: v })} />
    </div>
  );
}

// ── Activity Tab (lead stage) ─────────────────────────────────────────────

export function LeadCommunicationsTab({ quote, updateQuote }: {
  quote: Quote;
  updateQuote: (patch: QuotePatch) => Promise<unknown>;
}) {
  const { currentUser } = useUserProfile();
  const [activityType, setActivityType] = useState<LeadCommunicationType>('phone_call');
  const [note, setNote] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState(getDefaultFollowUp);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const entries = useMemo(() => {
    const log = quote.leadCommunicationLog ?? [];
    return [...log].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [quote.leadCommunicationLog]);

  async function handleAddEntry(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) { setError('Note is required.'); return; }
    setError('');
    setSaving(true);
    try {
      const newEntry: LeadCommunicationEntry = {
        id: crypto.randomUUID(),
        type: activityType,
        note: note.trim(),
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.uid ?? '',
        createdByName: currentUser?.displayName ?? currentUser?.email ?? 'Unknown',
      };
      const existing = quote.leadCommunicationLog ?? [];
      await updateQuote({
        leadCommunicationLog: [...existing, newEntry],
        ...(nextFollowUp ? { nextFollowUp } : {}),
      });
      setNote('');
      setActivityType('phone_call');
      setNextFollowUp(getDefaultFollowUp());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add activity');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-5">
      {/* Add Activity Form */}
      <div className="bg-white border border-stone rounded-xl px-4 py-4 shadow-brand-sm">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Log Communication</p>
        <form onSubmit={handleAddEntry} className="flex flex-col gap-3">
          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
              <p className="text-[12px] text-rose">{error}</p>
            </div>
          )}

          {/* Activity type selector */}
          <div className="flex flex-wrap gap-1.5">
            {ALL_COMMUNICATION_TYPES.map(t => {
              const Icon = COMMUNICATION_TYPE_ICON[t];
              const active = activityType === t;
              return (
                <button key={t} type="button" onClick={() => setActivityType(t)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-150
                    ${active ? 'bg-plum text-white' : 'bg-mist text-slate hover:text-midnight'}`}>
                  <Icon size={12} />
                  {LEAD_COMMUNICATION_TYPE_LABEL[t]}
                </button>
              );
            })}
          </div>

          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="What happened? Describe the interaction…" rows={3}
            className="w-full rounded-xl border border-stone bg-white px-4 py-3 text-small text-midnight
                       placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                       focus:border-plum resize-none" />

          <DateInput
            label="Next Follow-Up"
            value={nextFollowUp}
            onChange={setNextFollowUp}
            min={getTodayStr()}
          />

          <button type="submit" disabled={saving || !note.trim()}
            className="h-11 rounded-xl bg-plum text-white text-small font-semibold flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150">
            {saving ? <LoadingSpinner size="sm" /> : null}
            {saving ? 'Adding…' : 'Add Entry'}
          </button>
        </form>
      </div>

      {/* Activity Timeline */}
      {entries.length > 0 ? (
        <div className="relative pl-6">
          {/* Vertical timeline line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-stone" />

          {entries.map((entry, i) => {
            const Icon = COMMUNICATION_TYPE_ICON[entry.type] ?? MoreHorizontal;
            return (
              <div key={entry.id} className={`relative flex gap-3 ${i > 0 ? 'mt-4' : ''}`}>
                {/* Timeline dot */}
                <div className="absolute -left-6 top-0.5 w-[22px] h-[22px] rounded-full bg-white border-2 border-plum/30
                                flex items-center justify-center z-10">
                  <Icon size={10} className="text-plum" />
                </div>

                {/* Entry content */}
                <div className="flex-1 bg-white border border-stone rounded-xl px-3 py-2.5 shadow-brand-sm">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-plum">
                      {LEAD_COMMUNICATION_TYPE_LABEL[entry.type]}
                    </span>
                    <span className="text-[10px] text-slate" title={new Date(entry.createdAt).toLocaleString()}>
                      {relativeTime(entry.createdAt)}
                    </span>
                  </div>
                  <p className="text-small text-midnight leading-snug">{entry.note}</p>
                  <p className="text-[11px] text-slate mt-1">{entry.createdByName}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <ClipboardList size={32} className="mx-auto text-slate/30 mb-3" />
          <p className="text-[13px] text-slate">No communications logged yet</p>
          <p className="text-[12px] text-slate/60 mt-1">Use the form to log your first interaction</p>
        </div>
      )}
    </div>
  );
}

// ── Read-only Dashboard Cards (quote/job stages) ─────────────────────────

function QuoteStatusCard({ quote }: { quote: Quote }) {
  return (
    <DashCard title="Quote Status" icon={<FileText size={14} />}>
      <div className="divide-y divide-stone">
        {quote.quoteStatus && (
          <DataRow label="Status" value={
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${QUOTE_STATUS_CLASSES[quote.quoteStatus] ?? 'bg-stone text-slate'}`}>
              {QUOTE_STATUS_LABEL[quote.quoteStatus]}
            </span>
          } />
        )}
        <DataRow label="Quote Lead" value={quote.quoteLeadDisplayName} />
        <DataRow label="Estimate Visit" value={
          quote.estimateVisitDate
            ? new Date(quote.estimateVisitDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : null
        } />
        {quote.outcome && (
          <DataRow label="Outcome" value={quote.outcome.reason} />
        )}
      </div>
    </DashCard>
  );
}

function JobStatusCard({ quote }: { quote: Quote }) {
  return (
    <DashCard title="Job Status" icon={<Truck size={14} />}>
      <div className="divide-y divide-stone">
        {quote.jobStatus && (
          <DataRow label="Status" value={
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${JOB_STATUS_CLASSES[quote.jobStatus]}`}>
              {JOB_STATUS_LABEL[quote.jobStatus]}
            </span>
          } />
        )}
        <DataRow label="Move Lead" value={quote.moveLeadDisplayName} />
        <DataRow label="Crew Size" value={quote.formula?.finalCrewSize} />
        <DataRow label="Trucks" value={quote.formula?.truckQuantity} />
      </div>
    </DashCard>
  );
}

// ── Reconciliation Helpers ─────────────────────────────────────────────────

function ceilQuarter(n: number): number {
  return Math.ceil(n * 4) / 4;
}

function fmtHours(h: number): string {
  return h % 1 === 0 ? `${h}` : h.toFixed(2).replace(/0$/, '');
}

function fmtCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTransitDuration(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Stage Tracker ─────────────────────────────────────────────────────────

function StageTracker({ currentStage }: { currentStage: JobStage }) {
  const currentIdx = JOB_STAGES.indexOf(currentStage);
  const [tooltip, setTooltip] = useState<number | null>(null);
  const total = JOB_STAGES.length;

  return (
    <div className="relative w-full" style={{ height: 32 }}>
      {/* Background track */}
      <div className="absolute top-2 left-2 right-2 h-0.5 bg-stone" />
      {/* Filled track up to current stage */}
      {currentIdx > 0 && (
        <div
          className="absolute top-2 left-2 h-0.5 bg-plum"
          style={{ width: `calc(${(currentIdx / (total - 1)) * 100}% - 4px)` }}
        />
      )}

      {/* Dots */}
      {JOB_STAGES.map((s, i) => {
        const isComplete = i < currentIdx;
        const isCurrent = i === currentIdx;
        const isFuture = i > currentIdx;
        const pct = total > 1 ? (i / (total - 1)) * 100 : 0;

        return (
          <div
            key={s}
            className="absolute"
            style={{ left: `${pct}%`, top: 0, transform: 'translateX(-50%)' }}
          >
            <button
              type="button"
              className={`relative w-4 h-4 rounded-full border-2 transition-colors
                ${isComplete ? 'bg-plum border-plum' : ''}
                ${isCurrent ? 'bg-plum border-plum' : ''}
                ${isFuture ? 'bg-white border-stone' : ''}`}
              onClick={() => setTooltip(tooltip === i ? null : i)}
              aria-label={JOB_STAGE_LABEL[s]}
            >
              {isComplete && <Check size={10} className="absolute inset-0 m-auto text-white" />}
              {isCurrent && <Truck size={9} className="absolute inset-0 m-auto text-white" />}
            </button>
            {isCurrent && (
              <span className="absolute top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-bold text-plum">
                {JOB_STAGE_LABEL[s]}
              </span>
            )}
            {tooltip === i && !isCurrent && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setTooltip(null)} />
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-20 px-2 py-1 rounded-md bg-midnight text-white text-[10px] font-medium whitespace-nowrap shadow-lg">
                  {JOB_STAGE_LABEL[s]}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Hours Breakdown Tooltip ───────────────────────────────────────────────

function HoursBreakdownTooltip({ travelHours, jobDurationHours, totalHours, label }: {
  travelHours: number; jobDurationHours: number; totalHours: number; label: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex">
      <button type="button" onClick={() => setShow(v => !v)} onBlur={() => setTimeout(() => setShow(false), 150)}
        className="w-4 h-4 flex items-center justify-center rounded-full text-slate/60 hover:text-slate hover:bg-mist transition-colors"
        aria-label={`${label} hours breakdown`}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
        </svg>
      </button>
      {show && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-10 bg-midnight text-white text-[11px] rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
          <p className="font-semibold mb-1">{label} Breakdown</p>
          <p>Travel to/from site: {fmtHours(travelHours)} hr</p>
          <p>Job duration: {fmtHours(jobDurationHours)} hr</p>
          <p className="border-t border-white/20 mt-1 pt-1 font-semibold">Total: {fmtHours(totalHours)} hr</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-midnight" />
        </div>
      )}
    </span>
  );
}

// ── Request Payment Sheet ─────────────────────────────────────────────────

export function RequestPaymentSheet({
  job,
  orgId,
  quoteId,
  onSend,
  sending,
  onClose,
  onReconciled,
}: {
  job: Job;
  orgId: string;
  quoteId: string;
  onSend: (channels: ('sms' | 'email')[]) => Promise<void>;
  sending: boolean;
  onClose: () => void;
  onReconciled: () => void;
}) {
  const contact = job.pointOfContact;
  const hasEmail = !!contact?.email;
  const [emailChecked, setEmailChecked] = useState(hasEmail);
  const [reconcileChoice, setReconcileChoice] = useState<'quoted' | 'actual' | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const [editingActual, setEditingActual] = useState(false);
  const [editedActualHours, setEditedActualHours] = useState('');

  const outstanding = job.paymentSummary?.outstandingAmountCents ?? 0;
  const nextDue = job.paymentSummary?.nextPaymentDue;
  const dueNow = nextDue?.amountCents ?? outstanding;

  const ls = job.logisticsSummary;
  const times = job.actualJobTimes;
  const timestamps = times?.timestamps;

  const showReconciliation = !ls?.isFlatRate
    && (job.jobStage === 'pending_return' || job.jobStage === 'complete')
    && ls?.totalBillableHours != null
    && ls?.totalHourlyRate != null;

  const quotedTravelHours = ls?.travelTimeToJobHours ?? 0;
  const quotedTransitHours = ls?.finalTransitTimeHours ?? 0;
  const quotedJobDuration = (ls?.estimatedHours ?? 0) + quotedTransitHours;
  const quotedTotalHours = ls?.totalBillableHours ?? 0;
  const quotedCostCents = Math.round((ls?.finalCostEstimate ?? 0) * 100);
  const hourlyRate = ls?.totalHourlyRate ?? 0;

  let rawActualJobSeconds = 0;
  if (timestamps?.arrivedAtOrigin && timestamps?.unloadingComplete) {
    rawActualJobSeconds = Math.max(0,
      new Date(timestamps.unloadingComplete).getTime() - new Date(timestamps.arrivedAtOrigin).getTime()
    ) / 1000;
  }
  const actualJobDuration = ceilQuarter(rawActualJobSeconds / 3600);
  const actualTravelHours = quotedTravelHours;
  const computedActualTotalHours = ceilQuarter(actualTravelHours + actualJobDuration);

  const effectiveActualTotalHours = editedActualHours
    ? ceilQuarter(parseFloat(editedActualHours) || computedActualTotalHours)
    : computedActualTotalHours;
  const effectiveActualJobDuration = Math.max(0, effectiveActualTotalHours - actualTravelHours);
  const actualCostCents = Math.round(effectiveActualTotalHours * hourlyRate * 100);

  const deltaCents = actualCostCents - quotedCostCents;
  const deltaHours = effectiveActualTotalHours - quotedTotalHours;

  const canSend = emailChecked && !sending && (!showReconciliation || reconcileChoice !== null);

  async function handleReconcile(choice: 'quoted' | 'actual') {
    setReconcileChoice(choice);
    if (choice === 'actual' && deltaCents !== 0) {
      setReconciling(true);
      try {
        await api.post(`/jobs/${orgId}/${quoteId}/reconcile-hours`, { newTotalAmountCents: actualCostCents });
        onReconciled();
      } catch { /* silently fail */ } finally { setReconciling(false); }
    }
  }

  async function handleSend() {
    const channels: ('sms' | 'email')[] = [];
    if (emailChecked) channels.push('email');
    if (channels.length === 0) return;
    await onSend(channels);
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[90vh] overflow-y-auto
                      md:rounded-2xl md:max-w-md md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight">Request Payment</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"><X size={16} /></button>
        </div>

        {/* Reconcile Hours Worked */}
        {showReconciliation && (
          <div className="mb-4">
            <p className="text-[11px] font-bold text-slate uppercase tracking-wider mb-2.5">Reconcile Hours Worked</p>
            <div className="flex items-stretch gap-2">
              <div className={`flex-1 rounded-xl border p-3 flex flex-col items-center text-center transition-colors
                ${reconcileChoice === 'quoted' ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
                <p className="text-[10px] font-bold text-slate uppercase tracking-wider mb-1.5">Quoted</p>
                <p className="text-[20px] font-extrabold text-midnight leading-tight">{fmtHours(quotedTotalHours)} hr</p>
                <HoursBreakdownTooltip travelHours={quotedTravelHours} jobDurationHours={quotedJobDuration} totalHours={quotedTotalHours} label="Quoted" />
                <p className="text-[13px] font-semibold text-slate mt-1">{fmtCurrency(quotedCostCents)}</p>
              </div>
              <div className="flex items-center px-0.5"><ChevronRight size={18} className="text-slate/50" /></div>
              <div className={`flex-1 rounded-xl border p-3 flex flex-col items-center text-center transition-colors
                ${reconcileChoice === 'actual' ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
                <p className="text-[10px] font-bold text-slate uppercase tracking-wider mb-1.5">Actual</p>
                <div className="flex items-center gap-1">
                  <p className="text-[20px] font-extrabold text-midnight leading-tight">{fmtHours(effectiveActualTotalHours)} hr</p>
                  <button type="button" onClick={() => { setEditingActual(true); setEditedActualHours(effectiveActualTotalHours.toString()); }}
                    className="w-5 h-5 flex items-center justify-center rounded text-slate/50 hover:text-plum hover:bg-plum/10 transition-colors"
                    aria-label="Edit actual hours"><Pencil size={10} /></button>
                </div>
                <HoursBreakdownTooltip travelHours={actualTravelHours} jobDurationHours={effectiveActualJobDuration} totalHours={effectiveActualTotalHours} label="Actual" />
                <p className="text-[13px] font-semibold text-slate mt-1">{fmtCurrency(actualCostCents)}</p>
              </div>
            </div>
            {editingActual && (
              <div className="flex items-center gap-2 mt-2 px-1">
                <label className="text-[11px] text-slate font-medium shrink-0">Actual hours:</label>
                <input type="number" step="0.25" min="0" value={editedActualHours} onChange={e => setEditedActualHours(e.target.value)}
                  className="w-20 h-8 px-2 rounded-lg border border-stone bg-white text-[13px] text-midnight focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20" />
                <button type="button" onClick={() => setEditingActual(false)} className="text-[11px] text-plum font-semibold hover:text-rose transition-colors">Done</button>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button type="button" disabled={reconciling} onClick={() => handleReconcile('quoted')}
                className={`flex-1 h-10 rounded-xl text-[12px] font-medium transition-colors
                  ${reconcileChoice === 'quoted' ? 'border-2 border-plum bg-plum/5 text-plum' : 'border border-stone text-slate hover:border-slate hover:text-midnight'}`}>
                Keep quoted amount
              </button>
              <button type="button" disabled={reconciling} onClick={() => handleReconcile('actual')}
                className={`flex-1 h-10 rounded-xl text-[12px] font-semibold transition-all
                  ${reconcileChoice === 'actual' ? 'bg-gradient-to-r from-plum to-rose text-white shadow-sm' : 'bg-gradient-to-r from-plum to-rose text-white opacity-80 hover:opacity-100'}`}>
                {reconciling ? 'Updating...' : <>{deltaHours >= 0 ? '+' : ''}{fmtHours(deltaHours)} hr / {deltaCents >= 0 ? '+' : ''}{fmtCurrency(deltaCents)}</>}
              </button>
            </div>
          </div>
        )}

        {/* Due now */}
        <div className="bg-plum/5 border border-plum/20 rounded-xl p-4 text-center mb-4">
          <p className="text-[11px] font-bold text-slate uppercase tracking-wider mb-1">Due Now</p>
          <p className="text-[28px] font-extrabold text-midnight tracking-tight">{fmtCurrency(dueNow)}</p>
          {outstanding !== dueNow && outstanding > 0 && (
            <p className="text-[12px] text-slate mt-1">Remaining balance: {fmtCurrency(outstanding)}</p>
          )}
        </div>

        {/* Customer contact */}
        <div className="mb-4">
          <p className="text-[11px] font-bold text-slate uppercase tracking-wider mb-2">Customer Contact</p>
          <div className="flex flex-col gap-1.5 text-[13px]">
            {contact?.name && <p className="font-medium text-midnight">{contact.name}</p>}
            {contact?.email && <p className="text-slate flex items-center gap-1.5"><Mail size={12} className="text-plum" /> {contact.email}</p>}
            {contact?.phone && <p className="text-slate flex items-center gap-1.5"><Phone size={12} className="text-plum" /> {contact.phone}</p>}
          </div>
          {!hasEmail && <p className="text-[12px] text-rose mt-2">No customer email on file. Add an email to send payment requests.</p>}
        </div>

        {/* Channel selection */}
        <div className="mb-5">
          <p className="text-[11px] font-bold text-slate uppercase tracking-wider mb-2">Send Via</p>
          <label className={`flex items-center gap-3 h-12 px-4 rounded-xl border transition-colors cursor-pointer
            ${emailChecked ? 'border-plum bg-plum/5' : 'border-stone bg-white'}
            ${!hasEmail ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <input type="checkbox" checked={emailChecked} onChange={e => setEmailChecked(e.target.checked)} disabled={!hasEmail}
              className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
            <Mail size={15} className="text-slate" />
            <span className="text-[13px] font-medium text-midnight flex-1">Email</span>
            {!hasEmail && <span className="text-[11px] text-rose">No email</span>}
          </label>
        </div>

        {/* Send */}
        <button type="button" onClick={handleSend} disabled={!canSend}
          className="w-full h-12 rounded-xl bg-plum text-white font-semibold text-small
                     flex items-center justify-center gap-2
                     hover:bg-plum/90 active:bg-plum/80 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed">
          {sending ? 'Sending...' : <><Send size={15} /> Send Payment Request</>}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ── Card Ordering ──────────────────────────────────────────────────────────

export function getCardsForStage(quote: Quote, stage: LifecycleStage): React.ReactNode[] {
  const cards: React.ReactNode[] = [];

  switch (stage) {
    case 'lead':
      cards.push(<LeadInfoCard key="lead-info" quote={quote} />);
      cards.push(<ContactCard key="contact" quote={quote} />);
      cards.push(<AddressCard key="address" quote={quote} />);
      cards.push(<MoveDatesCard key="dates" quote={quote} />);
      if (quote.type === 'commercial') cards.push(<CompanyCard key="company" quote={quote} />);
      if (quote.type === 'residential') cards.push(<PropertyCard key="property" quote={quote} />);
      cards.push(<ActivityCard key="activity" quote={quote} />);
      cards.push(<NotesCard key="notes" quote={quote} />);
      // Show quote/job cards if they exist
      if (quote.quoteStatus) cards.push(<QuoteStatusCard key="quote-status" quote={quote} />);
      if (quote.formula) cards.push(<FormulaCard key="formula" quote={quote} />);
      if (quote.jobStatus) cards.push(<JobStatusCard key="job-status" quote={quote} />);
      break;

    case 'quote':
      cards.push(<QuoteStatusCard key="quote-status" quote={quote} />);
      cards.push(<FormulaCard key="formula" quote={quote} />);
      cards.push(<ContactCard key="contact" quote={quote} />);
      cards.push(<AddressCard key="address" quote={quote} />);
      cards.push(<InventoryCard key="inventory" quote={quote} />);
      cards.push(<MoveDatesCard key="dates" quote={quote} />);
      if (quote.type === 'commercial') cards.push(<CompanyCard key="company" quote={quote} />);
      if (quote.type === 'residential') cards.push(<PropertyCard key="property" quote={quote} />);
      cards.push(<LeadInfoCard key="lead-info" quote={quote} />);
      cards.push(<ActivityCard key="activity" quote={quote} />);
      cards.push(<NotesCard key="notes" quote={quote} />);
      if (quote.jobStatus) cards.push(<JobStatusCard key="job-status" quote={quote} />);
      break;

    case 'job':
      cards.push(<JobStatusCard key="job-status" quote={quote} />);
      cards.push(<CrewCard key="crew" quote={quote} />);
      cards.push(<ContactCard key="contact" quote={quote} />);
      cards.push(<AddressCard key="address" quote={quote} />);
      cards.push(<MoveDatesCard key="dates" quote={quote} />);
      cards.push(<FormulaCard key="formula" quote={quote} />);
      cards.push(<InventoryCard key="inventory" quote={quote} />);
      if (quote.type === 'commercial') cards.push(<CompanyCard key="company" quote={quote} />);
      if (quote.type === 'residential') cards.push(<PropertyCard key="property" quote={quote} />);
      cards.push(<QuoteStatusCard key="quote-status" quote={quote} />);
      cards.push(<LeadInfoCard key="lead-info" quote={quote} />);
      cards.push(<ActivityCard key="activity" quote={quote} />);
      cards.push(<NotesCard key="notes" quote={quote} />);
      break;
  }

  return cards.filter(Boolean);
}

// ── Main Page ──────────────────────────────────────────────────────────────

export function MovePage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;

  const { quote, loading, error, updateQuote, calculateTransit, calculateWarehouseTransits, refetch } = useQuote(orgId, quoteId ?? null);

  // Fetch job data when the quote is in the job stage (by explicit jobStatus OR lifecycleStage)
  const jobQuoteId = (quote?.jobStatus || quote?.lifecycleStage === 'job') ? (quoteId ?? null) : null;
  const { job, logJobIssue, updateJobStatus, updateJobStage, refetch: refetchJob } = useJob(orgId, jobQuoteId);

  // Fetch quote version count (for Client Quotes button text)
  const [versionsCount, setVersionsCount] = useState(0);
  useEffect(() => {
    if (!orgId || !quoteId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: unknown[] }>(
          `/quotes/${orgId}/${quoteId}/versions`,
        );
        if (!cancelled) setVersionsCount(res.data.length);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [orgId, quoteId]);

  // Org users for lead/move lead pickers
  const { users: orgUsers, loading: usersLoading } = useOrgAdminUsers(orgId ?? '');

  // Org settings for commercial ballpark / default units
  const { org: orgSettings } = useOrgSettings(orgId ?? '');

  // Warehouses for background transit calculation
  const { warehouses } = useOrgWarehouses(orgId ?? '');
  const warehousesRef = useRef(warehouses);
  warehousesRef.current = warehouses;

  const stage: LifecycleStage = quote?.lifecycleStage ?? (quote?.jobStatus ? 'job' : quote?.leadStatus ? 'lead' : 'quote');

  // Modal state
  const [showStatusSheet, setShowStatusSheet] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [userPickerMode, setUserPickerMode] = useState<'quoteLead' | 'moveLead'>('quoteLead');
  const [showEstimateSheet, setShowEstimateSheet] = useState(false);
  const [showActivitySheet, setShowActivitySheet] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);

  // ── Job in-progress state ───────────────────────────────────────────────
  const [startingJob, setStartingJob] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [confirmAdvance, setConfirmAdvance] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);

  // Sign-off state
  const [showSignOffCapture, setShowSignOffCapture] = useState(false);
  const [showWaiveSignOff, setShowWaiveSignOff] = useState(false);
  const [showSignOffDetail, setShowSignOffDetail] = useState(false);
  const [signOffSubmitting, setSignOffSubmitting] = useState(false);
  const [signOffError, setSignOffError] = useState<string | null>(null);

  // Payment request state
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  const { sending: paymentSending, sendRequest: sendPaymentRequest } = usePaymentRequest(orgId, quoteId ?? null);

  // ── Auto-fix: if job is in_progress but stage is "complete", promote to completed ──
  useEffect(() => {
    if (job?.jobStatus === 'in_progress' && job?.jobStage === 'complete') {
      const today = new Date().toISOString().split('T')[0];
      updateJobStatus('completed', today).then(() => Promise.all([refetchJob(), refetch()])).catch(err => {
        console.error('[auto-fix complete stage]', err);
      });
    }
  }, [job?.jobStatus, job?.jobStage, updateJobStatus, refetchJob, refetch]);

  // ── Start Job handler ───────────────────────────────────────────────────
  const handleStartJob = useCallback(async () => {
    if (!orgId || !quoteId) return;
    setStartingJob(true);
    try {
      await updateJobStatus('in_progress');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[start job]', err);
    } finally {
      setStartingJob(false);
    }
  }, [orgId, quoteId, updateJobStatus, refetchJob, refetch]);

  // ── Stage advance/revert handlers ───────────────────────────────────────
  const handleAdvance = useCallback(async () => {
    setConfirmAdvance(false);
    setAdvancing(true);
    try {
      await updateJobStage('advance');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[advance stage]', err);
    } finally {
      setAdvancing(false);
    }
  }, [updateJobStage, refetchJob, refetch]);

  const handleRevert = useCallback(async () => {
    setConfirmRevert(false);
    setReverting(true);
    try {
      await updateJobStage('revert');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[revert stage]', err);
    } finally {
      setReverting(false);
    }
  }, [updateJobStage, refetchJob, refetch]);

  // ── Sign-off handlers ──────────────────────────────────────────────────
  const handleRequestSignOff = useCallback(() => {
    setShowSignOffCapture(true);
  }, []);

  const handleSignOffCapture = useCallback(async (signerName: string, signatureDataUrl: string) => {
    if (!orgId || !quoteId) return;
    setSignOffSubmitting(true);
    setSignOffError(null);
    try {
      await api.patch(`/jobs/${orgId}/${quoteId}/signoff`, {
        action: 'capture',
        signerName,
        signatureImage: signatureDataUrl,
      });
      setShowSignOffCapture(false);
      setSignOffError(null);
      // Auto-advance from unloading to pending_return
      const currentStage = job?.jobStage ?? 'scheduled';
      if (currentStage === 'unloading') {
        try { await updateJobStage('advance'); } catch (err) { console.error('[signoff advance]', err); }
      }
      await refetchJob();
    } catch (err) {
      console.error('[signoff capture]', err);
      setSignOffError(err instanceof Error ? err.message : 'Failed to save sign-off. Please try again.');
    }
    setSignOffSubmitting(false);
  }, [orgId, quoteId, job?.jobStage, updateJobStage, refetchJob]);

  const handleSignOffWaive = useCallback(async (reason: string) => {
    if (!orgId || !quoteId) return;
    setSignOffSubmitting(true);
    try {
      await api.patch(`/jobs/${orgId}/${quoteId}/signoff`, {
        action: 'waive',
        waiveReason: reason,
      });
      setShowWaiveSignOff(false);
      // Auto-advance from unloading to pending_return
      const currentStage = job?.jobStage ?? 'scheduled';
      if (currentStage === 'unloading') {
        await updateJobStage('advance');
      }
      await refetchJob();
    } catch (err) {
      console.error('[signoff waive]', err);
    }
    setSignOffSubmitting(false);
  }, [orgId, quoteId, job?.jobStage, updateJobStage, refetchJob]);

  // ── Payment request handlers ───────────────────────────────────────────
  const handleSendPaymentRequest = useCallback(async (channels: ('sms' | 'email')[]) => {
    await sendPaymentRequest(channels);
    setShowPaymentSheet(false);
  }, [sendPaymentRequest]);

  const handleReconciled = useCallback(() => {
    refetchJob();
  }, [refetchJob]);

  // Save helper for editable cards
  const save = useCallback(async (patch: QuotePatch) => {
    try { await updateQuote(patch); } catch (err) { console.error('[unified lead save]', err); }
  }, [updateQuote]);

  // ── Transit auto-calculation ────────────────────────────────────────────
  const originPlaceId = quote?.addresses?.origin?.placeId;
  const destPlaceId = quote?.addresses?.destination?.placeId;
  const [calculatingTransit, setCalculatingTransit] = useState(false);
  const lastTransitKeyRef = useRef('');

  useEffect(() => {
    if (!originPlaceId || !destPlaceId) return;
    const key = `${originPlaceId}|${destPlaceId}`;
    if (key === lastTransitKeyRef.current) return;
    lastTransitKeyRef.current = key;

    setCalculatingTransit(true);
    calculateTransit(originPlaceId, destPlaceId)
      .then(() => {
        setCalculatingTransit(false);
        const wh = warehousesRef.current;
        if (wh.length) {
          calculateWarehouseTransits(
            wh.map(w => ({ id: w.id, placeId: w.placeId })),
            originPlaceId,
            destPlaceId,
          ).catch(err => console.error('[warehouse transit]', err));
        }
      })
      .catch(err => {
        lastTransitKeyRef.current = '';
        setCalculatingTransit(false);
        console.error('[transit calc]', err);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originPlaceId, destPlaceId, calculateTransit, calculateWarehouseTransits]);

  // ── Auto-calculate estimated value when inputs change ─────────────────
  const lastEstimateKeyRef = useRef('');
  useEffect(() => {
    if (!quote || !orgId) return;
    const hasOrigin = !!quote.addresses?.origin?.placeId || !!quote.originZipCode;
    const hasDest = !!quote.addresses?.destination?.placeId || !!quote.destinationZipCode;
    if (!hasOrigin || !hasDest) return;

    const key = [
      quote.type,
      quote.addresses?.origin?.placeId ?? '',
      quote.originZipCode ?? '',
      quote.addresses?.destination?.placeId ?? '',
      quote.destinationZipCode ?? '',
      quote.numberOfBedrooms ?? '',
      quote.squareFootage ?? '',
      quote.numberOfEmployees ?? '',
      quote.originStories ?? '',
      quote.destinationStories ?? '',
      quote.originElevatorAccess ?? '',
      quote.destinationElevatorAccess ?? '',
    ].join('|');

    if (key === lastEstimateKeyRef.current) return;
    lastEstimateKeyRef.current = key;

    const timer = setTimeout(() => {
      api.post<{ success: boolean; data: { estimatedValue: number | null } }>(
        `/quotes/${orgId}/${quote.id}/calculate-lead-estimate`,
      )
        .then(() => refetch())
        .catch(err => console.error('[lead estimate]', err));
    }, 800);
    return () => clearTimeout(timer);
  }, [
    quote?.type, quote?.id, orgId, refetch,
    quote?.addresses?.origin?.placeId, quote?.originZipCode,
    quote?.addresses?.destination?.placeId, quote?.destinationZipCode,
    quote?.numberOfBedrooms, quote?.squareFootage, quote?.numberOfEmployees,
    quote?.originStories, quote?.destinationStories,
    quote?.originElevatorAccess, quote?.destinationElevatorAccess,
    quote,
  ]);

  // Status change handler
  const handleStatusChange = useCallback(async (status: string) => {
    if (!quote) return;
    if (stage === 'lead') {
      await updateQuote({ leadStatus: status as LeadStatus });
    } else if (stage === 'quote') {
      await updateQuote({ quoteStatus: status as QuoteStatus });
    } else {
      await updateQuote({ jobStatus: status as JobStatus });
    }
  }, [quote, stage, updateQuote]);

  // Estimate scheduled handler
  const handleEstimateScheduled = useCallback(async (estimateDate: string, estimatorUid: string, _estimatorName: string) => {
    setShowEstimateSheet(false);
    try {
      const patch: Record<string, unknown> = {
        leadStatus: 'estimate_scheduled',
        lifecycleStage: 'quote',
        quoteStatus: 'draft',
      };
      if (estimateDate) patch.estimateVisitDate = estimateDate;
      if (estimatorUid) patch.quoteLeadUserId = estimatorUid;
      await updateQuote(patch as Parameters<typeof updateQuote>[0]);
      navigate(`/moves/${quoteId}`);
    } catch (err) {
      console.error('[estimate scheduled]', err);
    }
  }, [updateQuote, navigate, quoteId]);

  // Lead assignment handler
  const handleLeadAssign = useCallback(async (user: OrgUser) => {
    if (userPickerMode === 'quoteLead') {
      await updateQuote({ quoteLeadUserId: user.uid });
    } else {
      await updateQuote({ moveLeadUserId: user.uid });
    }
  }, [userPickerMode, updateQuote]);

  const openLeadPicker = useCallback(() => {
    setUserPickerMode(stage === 'job' ? 'moveLead' : 'quoteLead');
    setShowUserPicker(true);
  }, [stage]);

  // Current status for the status sheet
  const currentStatus = stage === 'lead'
    ? (quote?.leadStatus ?? 'new')
    : stage === 'quote'
      ? (quote?.quoteStatus ?? 'draft')
      : (quote?.jobStatus ?? 'scheduled');

  // Current lead UID for the user picker
  const currentLeadUid = stage === 'job'
    ? quote?.moveLeadUserId
    : quote?.quoteLeadUserId ?? quote?.createdBy;

  // Filter users for move lead picker (crew-eligible: movers, admins, field leads)
  const pickerUsers = stage === 'job'
    ? orgUsers.filter(u => u.status === 'active' && (u.role === 'mover' || u.role === 'admin' || u.role === 'field_lead'))
    : orgUsers.filter(u => u.status === 'active');

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <AlertCircle size={32} className="text-rose/50 mb-2" />
        <p className="text-small text-rose font-semibold">{error ?? 'Record not found'}</p>
        <button type="button" onClick={() => navigate(-1)}
          className="text-[13px] text-plum hover:underline mt-2">Go Back</button>
      </div>
    );
  }

  // For non-lead stages, use read-only dashboard cards
  const cards = stage !== 'lead' ? getCardsForStage(quote, stage) : null;
  const transit = quote.addresses?.transit ?? null;

  // ── Left column: hero card + nav cards (currently empty for lead) ────────
  const leftColumn = (
    <div className="flex flex-col gap-3">
      {/* Hero Card */}
      <HeroCard
        quote={quote}
        stage={stage}
        job={job}
        versionsCount={versionsCount}
        onStatusClick={() => setShowStatusSheet(true)}
        onLeadClick={openLeadPicker}
        onScheduleEstimate={() => setShowEstimateSheet(true)}
        onLogActivity={() => setShowActivitySheet(true)}
        onViewComms={() => navigate(`/moves/${quoteId}/communications`)}
        onLogIssue={() => setShowIssueModal(true)}
        onStartJob={handleStartJob}
        startingJob={startingJob}
        onViewSignOff={() => setShowSignOffDetail(true)}
        onAdvance={() => setConfirmAdvance(true)}
        onRevert={() => setConfirmRevert(true)}
        onRequestSignOff={handleRequestSignOff}
        onRequestPayment={() => setShowPaymentSheet(true)}
        advancing={advancing}
        reverting={reverting}
        updateQuote={updateQuote}
        navigate={navigate}
      />
    </div>
  );

  // ── Right column: main content ──────────────────────────────────────────
  const mainContent = (
    <>
      {/* ── Lead Stage: editable detail cards ──────────────────────── */}
      {stage === 'lead' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3">
          <EditableCustomerCard quote={quote} save={save} />
          <EditableCompanyCard quote={quote} save={save} org={orgSettings} />
          <EditableLeadInfoCard quote={quote} save={save} />
          <EditableMoveInfoCard quote={quote} save={save} />
          <EditableAddressesCard quote={quote} save={save} calculatingTransit={calculatingTransit} transit={transit} org={orgSettings} />
          <EditablePropertyDetailsCard quote={quote} save={save} orgId={orgId} />
          <EditableNotesCard quote={quote} save={save} />
        </div>
      )}

      {/* ── Quote/Job Stages: Read-only dashboard cards ────────────── */}
      {stage !== 'lead' && cards && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3">
          {cards}
        </div>
      )}
    </>
  );

  return (
    <div className="px-4 pt-4 pb-8 max-w-2xl lg:max-w-5xl mx-auto">
      {/* Back */}
      <button type="button" onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-3
                   transition-colors duration-150 -ml-1">
        <ArrowLeft size={16} />
        Back
      </button>

      {/* ── Mobile layout: stacked ─────────────────────────────────── */}
      <div className="lg:hidden flex flex-col gap-4">
        {leftColumn}
        {mainContent}
      </div>

      {/* ── Desktop layout: left panel (sticky) + right content ────── */}
      <div className="hidden lg:grid lg:grid-cols-[340px_1fr] lg:gap-6 lg:items-start">
        <div className="sticky top-4">
          {leftColumn}
        </div>
        <div>
          {mainContent}
        </div>
      </div>

      {/* Status Sheet */}
      <BottomSheet open={showStatusSheet} onClose={() => setShowStatusSheet(false)}
        title={`Change ${LIFECYCLE_STAGE_LABEL[stage]} Status`}>
        <StatusSheet
          stage={stage}
          currentStatus={currentStatus}
          onSelect={handleStatusChange}
          onClose={() => setShowStatusSheet(false)}
        />
      </BottomSheet>

      {/* User Picker */}
      <BottomSheet open={showUserPicker} onClose={() => setShowUserPicker(false)}
        title={userPickerMode === 'moveLead' ? 'Assign Move Lead' : 'Change Quote Lead'}>
        <UserPickerSheet
          users={pickerUsers}
          currentUid={currentLeadUid}
          onSelect={handleLeadAssign}
          onClose={() => setShowUserPicker(false)}
        />
      </BottomSheet>

      {/* Estimate Scheduled modal */}
      {showEstimateSheet && (
        <EstimateScheduledSheet
          users={orgUsers}
          usersLoading={usersLoading}
          onConfirm={handleEstimateScheduled}
          onClose={() => setShowEstimateSheet(false)}
        />
      )}

      {/* Communication Log modal */}
      {showActivitySheet && (
        <CommunicationLogSheet
          quote={quote}
          updateQuote={updateQuote}
          onClose={() => setShowActivitySheet(false)}
        />
      )}

      {/* Log Issue modal (job stage) */}
      <IssueLogModal
        isOpen={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        onSubmit={async (data: IssueFormData) => {
          await logJobIssue(data);
          setShowIssueModal(false);
        }}
      />

      {/* ── Job Progress Modals ─────────────────────────────────────── */}

      {/* Advance confirmation */}
      <ConfirmModal
        isOpen={confirmAdvance}
        title={job?.jobStage ? (JOB_STAGE_ACTION[job.jobStage] ?? 'Advance Stage') : 'Advance Stage'}
        message={`Are you sure you want to proceed to "${
          job?.jobStage
            ? JOB_STAGE_LABEL[JOB_STAGES[JOB_STAGES.indexOf(job.jobStage) + 1] ?? 'complete']
            : 'next stage'
        }"?`}
        confirmLabel={job?.jobStage ? (JOB_STAGE_ACTION[job.jobStage] ?? 'Continue') : 'Continue'}
        variant="primary"
        onConfirm={handleAdvance}
        onClose={() => setConfirmAdvance(false)}
      />

      {/* Revert confirmation */}
      <ConfirmModal
        isOpen={confirmRevert}
        title="Revert Stage"
        message={`Are you sure you want to go back to "${
          job?.jobStage
            ? JOB_STAGE_LABEL[JOB_STAGES[JOB_STAGES.indexOf(job.jobStage) - 1] ?? 'scheduled']
            : 'previous stage'
        }"?`}
        confirmLabel="Revert"
        variant="danger"
        onConfirm={handleRevert}
        onClose={() => setConfirmRevert(false)}
      />

      {/* Sign-Off Capture Modal */}
      <SignOffCaptureModal
        isOpen={showSignOffCapture}
        onConfirm={handleSignOffCapture}
        onWaive={() => {
          setShowSignOffCapture(false);
          setShowWaiveSignOff(true);
        }}
        onClose={() => setShowSignOffCapture(false)}
        submitting={signOffSubmitting}
        error={signOffError}
      />

      {/* Waive Sign-Off Modal */}
      <WaiveSignOffModal
        isOpen={showWaiveSignOff}
        onConfirm={handleSignOffWaive}
        onClose={() => setShowWaiveSignOff(false)}
        submitting={signOffSubmitting}
      />

      {/* Sign-Off Detail Modal */}
      {job?.customerSignOff && (
        <SignOffDetailModal
          isOpen={showSignOffDetail}
          signOff={job.customerSignOff}
          onCaptureSignOff={() => {
            setShowSignOffDetail(false);
            setShowSignOffCapture(true);
          }}
          onClose={() => setShowSignOffDetail(false)}
        />
      )}

      {/* Payment Request Sheet */}
      {showPaymentSheet && job && orgId && quoteId && (
        <RequestPaymentSheet
          job={job}
          orgId={orgId}
          quoteId={quoteId}
          onSend={handleSendPaymentRequest}
          sending={paymentSending}
          onClose={() => setShowPaymentSheet(false)}
          onReconciled={handleReconciled}
        />
      )}
    </div>
  );
}
